/**
 * Fase 13 — Firebase Realtime Database (client-side sync layer)
 *
 * Estratégia Offline-First:
 * - Dexie.js continua sendo o **primary store** (escrita/leitura local).
 * - Este módulo mantém um **mirror** em Firebase RTDB para multi-device.
 * - Se Firebase não estiver configurado, tudo continua funcionando offline.
 *
 * Config chaves (guardadas em db.config):
 *   - firebaseApiKey
 *   - firebaseDatabaseURL
 *   - firebaseProjectId
 *   - firebaseAuthDomain (opcional, default projectId + .firebaseapp.com)
 *   - firebaseEnabled (boolean; gate de on/off mesmo com keys preenchidas)
 *
 * Caminhos RTDB mirrorados (por deviceGroup):
 *   /{deviceGroup}/commands/{id}
 *   /{deviceGroup}/deliveryOrders/{id}
 *   /{deviceGroup}/kdsQueue/{id}
 *
 * `deviceGroup` permite isolar bancos (ex: por CNPJ/loja). Default: "kaven-default".
 */
import { initializeApp, deleteApp } from 'firebase/app';
import {
  getDatabase, ref, onValue, set, update, remove, off, get, goOnline, goOffline,
  runTransaction,
} from 'firebase/database';
import {
  getAuth, signInAnonymously, onAuthStateChanged, setPersistence, browserLocalPersistence,
} from 'firebase/auth';
import db from '@/db';
import { logAudit, AUDIT_ACTIONS } from '@/lib/audit';
import { getTombstonedIds, getTombstones, addTombstone, getTombstonedStableIds, getTombstoneTimes, tombstoneKillsRecord, removeTombstone, STABLE_ID_TABLES } from '@/lib/tombstones';

const SYNCED_TABLES = ['commands', 'deliveryOrders', 'kdsQueue', 'beverages', 'neighborhoods', 'menuItems'];

/**
 * Whitelist de chaves de `db.config` que SINCRONIZAM entre dispositivos.
 *
 * IMPORTANTE: a tabela `config` mistura coisas que DEVEM sincronizar
 * (config operacional do restaurante) com coisas LOCAIS do device
 * (uid, nickname, último error, etc). Por isso usamos whitelist explícita.
 *
 * Adicione aqui SOMENTE chaves que fazem sentido propagar pra todos
 * os celulares/desktops. NÃO inclua: deviceUid, deviceNickname,
 * firebaseApiKey, kavenLastError, *MigratedV2*, etc.
 */
const SHARED_CONFIG_KEYS = new Set([
  // Operação Buffet
  'buffetPricePerKg',
  'scaleBarcodePrefix',
  'scaleBarcodePluDigits',
  'scaleBarcodeMode',
  // Modo de operação do cardápio (Padrão / Fim de Semana / Evento)
  'activePreset',
  'menuPresets',
  // Bloqueios e regras de negócio
  'requireOpenSession',
  'trainingMode',
  // Identidade visual / informações compartilhadas
  'businessName',
  'businessAddress',
  'businessPhone',
  // Taxa de serviço, troco padrão, etc
  'serviceChargePct',
  'minOrderDelivery',
  'serviceCharge',
  // Z-API / WhatsApp (chave pública, instance compartilhado)
  'zapiInstanceId',
  'zapiInstanceToken',
  'zapiClientToken',
  // Pix (chave compartilhada)
  'pixKey',
  'pixPayload',
  // Cardápio digital
  'menuPublicUrl',
]);
const SYNC_APP_NAME = 'kaven-sync';

// Cache de merges idempotentes por sessão: chave = `${table}#${id}` → updatedAt
// já mergeado. Evita reprocessar a mesma versão remota em loop quando o RTDB
// dispara onValue múltiplas vezes (latência, retry, listener recreado).
const mergedVersions = new Map();
// Cache de resoluções de conflito já aplicadas neste boot, p/ evitar deletes
// repetidos do mesmo number quando dados poluídos no remote continuam chegando.
const resolvedConflicts = new Set();

// v7.78 — Marca quando um write local esta apenas espelhando um valor que
// JA veio do Firebase (attachTableListener aplicando LWW), em vez de ser
// uma mutacao local genuina. Os hooks 'creating'/'updating' do Dexie abaixo
// checam esta flag pra NAO reenviar o mesmo dado de volta pro Firebase com
// um updatedAt novo -- sem isso, qualquer device que so estivesse re-
// sincronizando historico (reconectou, resetou, entrou pela 1a vez) fazia
// um registro antigo e ja concluido parecer "acabou de mudar" pra TODOS os
// outros devices, que por sua vez tambem re-aplicavam e re-carimbavam com
// SEU proprio now() -- um efeito cascata que fazia pedidos ja prontos ha
// semanas reaparecerem "do nada", em loop, entre os aparelhos (ticket
// #207625 -- causa raiz do padrao cronico de "pedidos fantasmas").
// v7.82 — CONTADOR de profundidade (era boolean). Callbacks de tabelas
// diferentes rodam CONCORRENTES e intercalam em cada await: o `finally` de
// um zerava o boolean ENQUANTO o put() do outro ainda não tinha executado a
// transação → hook disparava → re-push com updatedAt novo → onValue → merge
// → put... CASCATA INFINITA de re-stamp (medido: 844 merges em 25s sem
// nenhuma escrita externa; kdsQueue/menuItems starved — pedidos pagos só
// apareciam com F5). Com contador, o guard só libera quando NENHUM merge
// remoto está em voo.
let applyingRemoteSyncDepth = 0;

// Cache local do nickname — consultado pelos hooks de write
let cachedNickname = '';

export async function refreshDeviceNicknameCache() {
  try {
    const cfg = await db.config.get('deviceNickname');
    cachedNickname = (cfg?.value || '').toString();
  } catch {
    cachedNickname = '';
  }
  return cachedNickname;
}

export function getCachedNickname() {
  return cachedNickname;
}

let currentApp = null;
let currentDb = null;
let currentAuth = null;
let currentUid = null;
let authUnsub = null;
let connectionListener = null;
let tableListeners = {}; // table -> unsubscribe fn
let isConnected = false;
let isAuthenticated = false;
let remoteReceivedFlag = false;
let lastAuthError = null; // Hotfix: expõe erro de auth pra UI (badge/tooltip)
const listeners = new Set(); // listeners de estado

/**
 * Fase 31 — Painel de Saúde do Sync.
 * Mantém o último timestamp (ms) de push/receive por "domínio" sincronizado.
 * Domínios públicos hoje: `menuItems` (cardápio) e `config` (configurações
 * via __sharedConfig__/whitelist). Outras tabelas podem ser agregadas
 * depois sem mudar o contrato — chave é o nome da tabela do Dexie.
 */
const lastSyncMs = {
  menuItems: { push: 0, receive: 0 },
  config: { push: 0, receive: 0 },
};
function markSync(domain, kind) {
  if (!lastSyncMs[domain]) lastSyncMs[domain] = { push: 0, receive: 0 };
  lastSyncMs[domain][kind] = Date.now();
  emit();
}

/**
 * authReady — Promise global que resolve assim que `signInAnonymously()`
 * completa (sucesso OU falha conhecida). TODAS as operações de read/write
 * no Firebase devem `await waitForAuth()` antes de chamar `set/get/remove`.
 *
 * Sem isso, snapshots iniciais, flushTombstones, listeners attach e até o
 * wipeRemote podem disparar com `auth.currentUser = null` → PERMISSION_DENIED
 * silencioso, retry loop, ou pior, escrita no nó errado.
 *
 * Reset em cada `teardownFirebaseSync` para permitir reconexão limpa.
 */
let authReadyResolve = null;
let authReadyPromise = new Promise((resolve) => { authReadyResolve = resolve; });

/**
 * Aguarda o auth anônimo terminar. Resolve mesmo se o auth falhar
 * (consumer decide se prossegue baseado em `isAuthenticated`).
 * Timeout de segurança: 8s → resolve mesmo assim para não travar a UI.
 */
export async function waitForAuth(timeoutMs = 8000) {
  if (isAuthenticated) return true;
  try {
    await Promise.race([
      authReadyPromise,
      new Promise((resolve) => setTimeout(resolve, timeoutMs)),
    ]);
  } catch {}
  return isAuthenticated;
}

function markAuthReady() {
  if (authReadyResolve) {
    authReadyResolve();
    authReadyResolve = null;
  }
}

function resetAuthReady() {
  authReadyPromise = new Promise((resolve) => { authReadyResolve = resolve; });
}

/**
 * Busca e valida config Firebase do IndexedDB.
 */
export async function getFirebaseConfig() {
  const keys = [
    'firebaseApiKey',
    'firebaseDatabaseURL',
    'firebaseProjectId',
    'firebaseAuthDomain',
    'firebaseStorageBucket',
    'firebaseMessagingSenderId',
    'firebaseAppId',
    'firebaseEnabled',
    'deviceGroup',
  ];
  const results = await Promise.all(keys.map((k) => db.config.get(k)));
  const [apiKey, databaseURL, projectId, authDomain, storageBucket, messagingSenderId, appId, enabled, deviceGroup] = results.map((r) => r?.value || '');
  return {
    apiKey,
    databaseURL,
    projectId,
    authDomain: authDomain || (projectId ? `${projectId}.firebaseapp.com` : ''),
    storageBucket: storageBucket || '',
    messagingSenderId: messagingSenderId || '',
    appId: appId || '',
    enabled: enabled === true || enabled === 'true',
    deviceGroup: deviceGroup || 'kaven-default',
  };
}

export function isFirebaseConnected() {
  return isConnected;
}

// ════════════════════════════════════════════════════════════════════════
// v7.69 — Rastreio de escritas Dexie disparadas por listeners do Firebase.
// db.close() (versionchange/resetDatabase) deve AGUARDAR esse contador
// zerar antes de fechar; sem isso, callbacks onValue em curso estouravam
// ~129 DatabaseClosedError por page load (beverages/kdsQueue/neighborhoods).
// ════════════════════════════════════════════════════════════════════════
let activeListenerWrites = 0;

export async function waitForListenerWritesToDrain(maxMs = 2000) {
  const start = Date.now();
  while (activeListenerWrites > 0 && Date.now() - start < maxMs) {
    await new Promise((r) => setTimeout(r, 100));
  }
  return activeListenerWrites === 0;
}

/**
 * v7.68 — Recarrega as chaves do Firebase do backend (`/api/config`) e
 * regrava na tabela `db.config` local. Chamado no início de todo
 * `initFirebaseSync()` — garante que um reset do Dexie local não deixe o
 * sync permanentemente desativado por falta de credenciais.
 * Se o device desativou `firebaseEnabled` explicitamente, respeita; se a
 * chave sumiu (pós-reset) e o backend tem credenciais, reativa.
 */
export async function refreshFirebaseConfigFromServer() {
  const apiUrl = process.env.REACT_APP_BACKEND_URL;
  if (!apiUrl) return false;
  try {
    const r = await fetch(`${apiUrl}/api/config`);
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const body = await r.json();
    const remote = body?.config || {};
    const keys = [
      'firebaseApiKey', 'firebaseAuthDomain', 'firebaseDatabaseURL',
      'firebaseProjectId', 'firebaseStorageBucket', 'firebaseMessagingSenderId',
      'firebaseAppId', 'deviceGroup',
    ];
    const rows = [];
    for (const k of keys) {
      const v = String(remote[k] ?? '').trim();
      if (!v) continue;
      // Guard: 'kaven-default' é o namespace legado — NUNCA sobrescreve um
      // deviceGroup local válido (kaven-XXXX) com ele, senão o device muda
      // de namespace no RTDB e "perde" todos os dados sincronizados.
      if (k === 'deviceGroup' && v === 'kaven-default') {
        const local = await db.config.get('deviceGroup');
        if (local?.value && local.value !== 'kaven-default') continue;
      }
      rows.push({ key: k, value: v });
    }
    if (rows.length) await db.config.bulkPut(rows);
    const hasCore = remote.firebaseApiKey && remote.firebaseDatabaseURL && remote.firebaseProjectId;
    if (hasCore) {
      const enabledRow = await db.config.get('firebaseEnabled');
      if (enabledRow === undefined || enabledRow?.value === undefined || enabledRow?.value === '') {
        await db.config.put({ key: 'firebaseEnabled', value: true });
      }
    }
    return rows.length > 0;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[FIREBASE SYNC] refreshFirebaseConfigFromServer falhou (usando Dexie local):', err?.message || err);
    return false;
  }
}

/**
 * v7.43 — Expõe o app Firebase atual (mesmo handle usado pelo RTDB) para
 * que o módulo `firebaseStorage.js` consiga construir um Storage handle
 * reutilizando a mesma autenticação anônima já estabelecida. Sem isso,
 * Storage criaria um app paralelo e replicaria signInAnonymously.
 *
 * Retorna `null` se o sync ainda não foi inicializado ou se Firebase
 * está desabilitado nas configurações.
 */
export function getFirebaseApp() {
  return currentApp;
}

/**
 * v7.43 — Acesso conveniente ao deviceGroup já carregado, sem precisar
 * re-ler do Dexie. Usado para namespacing dos paths no Storage
 * (`menuImages/{deviceGroup}/...`).
 */
export async function getDeviceGroup() {
  const cfg = await getFirebaseConfig();
  return cfg.deviceGroup || 'kaven-default';
}

/**
 * v7.40 — Verifica se há `menuItems` remotos no Firebase RTDB.
 *
 * Usado pelas migrations de seed do db.js: se o Firebase já tem cardápio
 * publicado em `{deviceGroup}/menuItems`, NÃO devemos re-inserir o seed
 * local (caso contrário, pratos deletados pelo admin voltam quando um
 * novo device abre o app pela primeira vez).
 *
 * Retorna `false` se Firebase não está inicializado/conectado/autenticado
 * — fluxo seguro: faz seed local (cenário primeira instalação).
 *
 * @param {number} timeoutMs
 * @returns {Promise<boolean>} true se há ≥1 menuItem remoto não-vazio
 */
export async function peekRemoteMenuItems(timeoutMs = 1500) {
  try {
    // v7.46 — Lazy-init do Firebase auth ANTES da query. Sem isso, esta
    // função (chamada pelo seedDatabase no boot crítico) sempre retornava
    // false porque `initFirebaseSync` roda em background depois do seed.
    // Resultado: o seed re-criava menus padrão por cima de exclusões
    // legítimas. Com o ensureFirebaseAuth(), o peek faz seu próprio
    // bootstrap mínimo de auth (não anexa listeners; só faz signIn).
    const authRes = await ensureFirebaseAuth();
    if (!authRes.ok) return false;
    if (!currentDb || !isAuthenticated) return false;
    const cfg = await getFirebaseConfig();
    const group = cfg.deviceGroup || 'kaven-default';
    const r = ref(currentDb, `${group}/menuItems`);
    const snap = await Promise.race([
      get(r),
      new Promise((res) => setTimeout(() => res(null), timeoutMs)),
    ]);
    if (!snap || !snap.exists?.()) return false;
    const val = snap.val();
    if (!val || typeof val !== 'object') return false;
    // Conta entradas não-vazias (ignora chaves residuais com null)
    const keys = Object.keys(val).filter((k) => val[k] && typeof val[k] === 'object');
    return keys.length > 0;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[FBSYNC] peekRemoteMenuItems falhou:', e?.message || e);
    return false;
  }
}

export function getSyncState() {
  return {
    connected: isConnected,
    initialized: !!currentApp,
    authenticated: isAuthenticated,
    uid: currentUid,
    remoteReceived: remoteReceivedFlag,
    authError: lastAuthError,
    lastSync: {
      menuItems: { ...lastSyncMs.menuItems },
      config: { ...lastSyncMs.config },
    },
  };
}

export function subscribeSyncState(fn) {
  listeners.add(fn);
  fn(getSyncState());
  return () => listeners.delete(fn);
}

function emit() {
  const state = getSyncState();
  listeners.forEach((fn) => { try { fn(state); } catch {} });
}

// v7.46 — Guarda para garantir que `ensureFirebaseAuth` seja idempotente
// quando chamado em paralelo (seed dispara, initFirebaseSync dispara logo
// depois). Sem isso, dois `initializeApp(SYNC_APP_NAME)` rodariam em sequência
// e perderíamos auth state.
let ensureAuthInflight = null;

/**
 * v7.46 — Bootstrap MÍNIMO do Firebase: só inicializa o app e faz
 * anonymous sign-in. NÃO anexa listeners, NÃO faz push de snapshots.
 *
 * Motivo: a função `peekRemoteMenuItems()` é chamada pelo `seedDatabase`
 * ANTES de `initFirebaseSync` (porque o seed roda no boot crítico, sync
 * roda em background). Sem este lazy-init, o peek sempre retornava `false`
 * (Firebase não autenticado) e a seed re-criava o menu padrão por cima
 * de pratos que o admin já havia excluído em outro device.
 *
 * É idempotente: se já autenticado, retorna noop. Se outro chamador está
 * com auth em andamento, awaita o mesmo inflight promise.
 *
 * Não é destrutivo — diferente de `initFirebaseSync()` que faz
 * `teardownFirebaseSync()` no início. Este só CRIA, nunca destrói.
 */
export async function ensureFirebaseAuth() {
  // Já autenticado? Noop.
  if (currentApp && isAuthenticated) return { ok: true, reason: 'already_authed' };
  // Já em andamento? Cole na mesma promise.
  if (ensureAuthInflight) return ensureAuthInflight;

  ensureAuthInflight = (async () => {
    const cfg = await getFirebaseConfig();
    if (!cfg.enabled || !cfg.apiKey || !cfg.databaseURL || !cfg.projectId) {
      return { ok: false, reason: 'disabled_or_incomplete' };
    }
    try {
      // Reutiliza o app se já existir (mesmo SYNC_APP_NAME). initializeApp
      // lança "duplicate-app" se tentarmos recriar; capturamos e seguimos.
      if (!currentApp) {
        try {
          currentApp = initializeApp(
            {
              apiKey: cfg.apiKey,
              authDomain: cfg.authDomain,
              databaseURL: cfg.databaseURL,
              projectId: cfg.projectId,
              storageBucket: cfg.storageBucket || undefined,
              messagingSenderId: cfg.messagingSenderId || undefined,
              appId: cfg.appId || undefined,
            },
            SYNC_APP_NAME,
          );
        } catch (e) {
          // Já existe? Reusa.
          if (e?.code === 'app/duplicate-app') {
            const { getApp } = await import('firebase/app');
            currentApp = getApp(SYNC_APP_NAME);
          } else {
            throw e;
          }
        }
        currentDb = getDatabase(currentApp);
        currentAuth = getAuth(currentApp);
        try {
          await setPersistence(currentAuth, browserLocalPersistence);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('[FIREBASE AUTH] setPersistence falhou (ignorado):', e?.message || e);
        }
      }
      if (!isAuthenticated) {
        // v7.48 — Retry com backoff exponencial em signInAnonymously.
        // Firebase Auth falha intermitentemente com `auth/network-request-failed`
        // em redes flutuantes (mobile, WiFi público, primeira conexão fria).
        // Antes: falha na 1ª tentativa derrubava o lazy auth pra sempre
        // (até reload). Agora: 3 tentativas em 1s/2s/4s (total ~7s no pior caso).
        let cred = null;
        let lastErr = null;
        const delays = [1000, 2000, 4000];
        for (let attempt = 0; attempt < delays.length; attempt += 1) {
          try {
            cred = await signInAnonymously(currentAuth);
            break;
          } catch (e) {
            lastErr = e;
            const code = e?.code || '';
            // Erros não-retriáveis: aborta imediatamente.
            if (code === 'auth/admin-restricted-operation' ||
                code === 'auth/operation-not-allowed' ||
                code === 'auth/api-key-not-valid') {
              break;
            }
            // eslint-disable-next-line no-console
            console.warn(`[FIREBASE AUTH lazy] tentativa ${attempt + 1}/3 falhou (${code || e?.message}); aguardando ${delays[attempt]}ms.`);
            if (attempt < delays.length - 1) {
              await new Promise((r) => setTimeout(r, delays[attempt]));
            }
          }
        }
        if (!cred) {
          throw lastErr || new Error('signInAnonymously: todas as tentativas falharam');
        }
        isAuthenticated = true;
        currentUid = cred?.user?.uid || currentAuth.currentUser?.uid || null;
        lastAuthError = null;
        if (currentUid) {
          try { await db.config.put({ key: 'deviceUid', value: currentUid }); } catch {}
        }
        // eslint-disable-next-line no-console
        console.log('[FIREBASE AUTH lazy] Anônimo OK — uid:', currentUid);
        markAuthReady();
      }
      emit();
      return { ok: true, reason: 'inited' };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[FIREBASE AUTH lazy] falhou:', err?.code || err?.message || err);
      return { ok: false, reason: 'auth_failed', error: err?.message || String(err) };
    } finally {
      // Permite re-tentar em chamadas futuras (mas se já autenticado, a guarda no topo cuida).
      ensureAuthInflight = null;
    }
  })();

  return ensureAuthInflight;
}

/**
 * Inicializa (ou re-inicializa) o app Firebase a partir do config atual.
 * Seguro chamar múltiplas vezes (destrói o anterior).
 */
// v7.82 — Single-flight: inits CONCORRENTES (boot do App + mount de página +
// recovery do versionchange) causavam corrida fatal — o 2º init rodava
// teardownFirebaseSync() → deleteApp() → currentDb=null ENQUANTO os listeners
// do 1º processavam (erro '_checkNotDeleted', ponte backendOrders morta,
// pedidos pagos invisíveis até F5). Agora chamadas simultâneas aguardam a
// mesma promise; re-inits sequenciais continuam funcionando.
let _initInflight = null;

export async function initFirebaseSync() {
  if (_initInflight) return _initInflight;
  _initInflight = _initFirebaseSyncInner().finally(() => { _initInflight = null; });
  return _initInflight;
}

async function _initFirebaseSyncInner() {
  // v7.68 — Recarrega a config do Firebase do BACKEND e regrava no Dexie
  // antes de ler. Sem isso, um reset do banco local apagava as credenciais
  // e o sync ficava desativado silenciosamente (uid: null, sem erro visível).
  try {
    await Promise.race([
      refreshFirebaseConfigFromServer(),
      new Promise((resolve) => setTimeout(resolve, 4000)),
    ]);
  } catch {}
  const cfg = await getFirebaseConfig();
  if (!cfg.enabled || !cfg.apiKey || !cfg.databaseURL || !cfg.projectId) {
    // eslint-disable-next-line no-console
    console.log('[FIREBASE SYNC] Desabilitado ou config incompleto — rodando só local (Dexie).');
    emit();
    return { ok: false, reason: 'disabled_or_incomplete' };
  }

  // v7.46 — Se o app já foi inicializado pela rota lazy (`ensureFirebaseAuth`
  // disparada pelo seed via `peekRemoteMenuItems`), REUSAMOS o app + auth.
  // Sem isso, faríamos teardown+reauth dobrado a cada boot, perdendo o
  // benefício do lazy init. Listeners + push ainda rodam normalmente abaixo.
  const reuseLazy = !!currentApp && isAuthenticated;
  if (!reuseLazy) {
    await teardownFirebaseSync(); // limpa state anterior
  }

  try {
    if (!reuseLazy) {
      currentApp = initializeApp(
        {
          apiKey: cfg.apiKey,
          authDomain: cfg.authDomain,
          databaseURL: cfg.databaseURL,
          projectId: cfg.projectId,
          storageBucket: cfg.storageBucket || undefined,
          messagingSenderId: cfg.messagingSenderId || undefined,
          appId: cfg.appId || undefined,
        },
        SYNC_APP_NAME,
      );
      currentDb = getDatabase(currentApp);
      currentAuth = getAuth(currentApp);
    }

    // Listener de conexão (independe de auth) — anexa só se ainda não tem
    if (!connectionListener) {
      const connRef = ref(currentDb, '.info/connected');
      connectionListener = onValue(connRef, (snap) => {
        const wasConnected = isConnected;
        isConnected = snap.val() === true;
        emit();
        // v7.49 — Keepalive watchdog: ao detectar transição
        // connected→disconnected, agenda goOnline() em 5s. Idempotente
        // (Firebase ignora goOnline se já está reconectando). Junto com
        // visibilitychange handler abaixo, cobre os 2 cenários de queda:
        //   (a) idle long enough para servidor cortar (1h+)
        //   (b) device adormeceu e voltou (mobile)
        if (wasConnected && !isConnected) {
          // eslint-disable-next-line no-console
          console.warn('[FIREBASE SYNC] Conexão CAIU — agendando goOnline em 5s.');
          setTimeout(() => {
            if (currentDb && !isConnected) {
              try {
                goOnline(currentDb);
                // eslint-disable-next-line no-console
                console.log('[FIREBASE SYNC] goOnline() disparado (keepalive watchdog).');
              } catch (e) {
                // eslint-disable-next-line no-console
                console.warn('[FIREBASE SYNC] goOnline falhou:', e?.message || e);
              }
            }
          }, 5000);
        }
      });
    }

    // v7.49 — Keepalive em duas frentes:
    //   (1) visibilitychange: ao voltar pra aba após adormecer, força goOnline
    //   (2) heartbeat periódico (60s): se isConnected=false, force-reconnect
    if (typeof window !== 'undefined' && !window._kavenFbWatchdogInstalled) {
      window._kavenFbWatchdogInstalled = true;
      const onVisibilityRestore = () => {
        if (document.visibilityState === 'visible' && currentDb && !isConnected) {
          // eslint-disable-next-line no-console
          console.log('[FIREBASE SYNC] Aba voltou — forçando goOnline.');
          try { goOnline(currentDb); } catch {}
        }
      };
      document.addEventListener('visibilitychange', onVisibilityRestore);
      // Heartbeat: a cada 60s, se desconectado, tenta reconectar
      setInterval(() => {
        if (currentDb && !isConnected) {
          try { goOnline(currentDb); } catch {}
        }
      }, 60000);
    }

    if (!reuseLazy) {
      // === Fase 15 — Anonymous Auth antes de qualquer read/write ===
      // Persistência local para reusar o mesmo uid em sessões futuras
      try {
        await setPersistence(currentAuth, browserLocalPersistence);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[FIREBASE AUTH] setPersistence falhou (ignorado):', e?.message || e);
      }

      // Observa mudanças de estado para manter sync state atualizado
      authUnsub = onAuthStateChanged(currentAuth, async (user) => {
        const wasAuth = isAuthenticated;
        isAuthenticated = !!user;
        currentUid = user?.uid || null;
        if (user?.uid) {
          // Persiste deviceUid localmente para auditoria futura
          try { await db.config.put({ key: 'deviceUid', value: user.uid }); } catch {}
        }
        emit();
        // Hotfix v5: ao autenticar (transição false→true), drena writes
        // que ficaram enfileirados durante o boot ou após PERMISSION_DENIED.
        if (!wasAuth && isAuthenticated) flushPendingWrites();
      });

      // Signa anônimo (idempotente — reusa o user se já existir)
      // v7.48 — Retry com backoff exponencial (1s, 2s, 4s) pra resiliência
      // contra `auth/network-request-failed` em redes flutuantes.
      try {
        let cred = null;
        let lastErr = null;
        const delays = [1000, 2000, 4000];
        for (let attempt = 0; attempt < delays.length; attempt += 1) {
          try {
            cred = await signInAnonymously(currentAuth);
            break;
          } catch (e) {
            lastErr = e;
            const code = e?.code || '';
            if (code === 'auth/admin-restricted-operation' ||
                code === 'auth/operation-not-allowed' ||
                code === 'auth/api-key-not-valid') {
              break;
            }
            // eslint-disable-next-line no-console
            console.warn(`[FIREBASE AUTH] tentativa ${attempt + 1}/3 falhou (${code || e?.message}); aguardando ${delays[attempt]}ms.`);
            if (attempt < delays.length - 1) {
              await new Promise((r) => setTimeout(r, delays[attempt]));
            }
          }
        }
        if (!cred) throw lastErr || new Error('signInAnonymously: todas as tentativas falharam');
        isAuthenticated = true;
        currentUid = cred?.user?.uid || currentAuth.currentUser?.uid || null;
        lastAuthError = null;
        if (currentUid) {
          try { await db.config.put({ key: 'deviceUid', value: currentUid }); } catch {}
        }
        // eslint-disable-next-line no-console
        console.log('[FIREBASE AUTH] Anônimo OK — uid:', currentUid);
        // Hotfix v6: libera todos os awaiters do authReady.
        markAuthReady();
        // Hotfix v5: drena a fila assim que o auth resolver.
        flushPendingWrites();
      } catch (err) {
        const code = err?.code || '';
        const msg = err?.message || String(err);
        // Hotfix: mensagem amigável por código de erro conhecido
        let friendly = msg;
        if (code === 'auth/admin-restricted-operation' || code === 'auth/operation-not-allowed') {
          friendly = 'Login anônimo desativado no Firebase. Vá em Authentication → Sign-in method → Anonymous → Habilitar.';
        } else if (code === 'auth/network-request-failed') {
          friendly = 'Sem conexão com Firebase Auth — verifique internet.';
        } else if (code === 'auth/api-key-not-valid') {
          friendly = 'API key inválida em /config. Confira firebaseApiKey.';
        }
        lastAuthError = { code, message: friendly };
        isAuthenticated = false;
        // eslint-disable-next-line no-console
        console.error('[FIREBASE AUTH] signInAnonymously falhou:', code || msg, '→', friendly);
        // Hotfix v6: mesmo em erro, libera awaiters — eles checam `isAuthenticated`
        // e decidem se prosseguem ou enfileiram. Sem isso a UI travaria 8s no timeout.
        markAuthReady();
      }
    } else {
      // eslint-disable-next-line no-console
      console.log('[FIREBASE SYNC] Reusando app+auth do lazy init (uid:', currentUid, '). Pulando teardown.');
      // flushPendingWrites já foi disparado no lazy init; replicamos por
      // segurança caso algo tenha sido enfileirado entre as duas chamadas.
      flushPendingWrites();
    }

    // Liga listeners DEPOIS do auth (para respeitar regras auth != null)
    for (const table of SYNCED_TABLES) {
      attachTableListener(cfg.deviceGroup, table);
    }
    // Listener do bucket de configurações compartilhadas.
    attachSharedConfigListener(cfg.deviceGroup);
    // v7.71 — Ponte MongoDB->Firebase: pedidos promovidos pelo backend
    // (webhook/reconciler) quando o navegador de origem ja fechou.
    attachBackendOrdersListener(cfg.deviceGroup);
    // v7.76 — Garante que writes enfileirados (falhas nao-permission-denied)
    // ganhem novas tentativas periodicas, nao so na transicao de auth.
    startPendingWritesFlushInterval();

    // Hotfix v7.6 — Listener global de tombstones replicados (__tombstones__).
    // Quando outro device exclui um item, este recebe a marca e auto-limpa.
    // v7.11 — aguardamos o PRIMEIRO snapshot antes de prosseguir, para que
    // os tombstones remotos sejam ingeridos ANTES de subir o snapshot local
    // (eliminando a race condition que ressuscitava itens excluídos).
    const tombstonesReady = attachTombstonesListener(cfg.deviceGroup);

    // v7.44 — Resiliência no boot do sync. Cada passo abaixo é envolvido em
    // try/catch + timeout individual. Se um deles travar/falhar (rede ruim,
    // RTDB lento, rules denied), os outros continuam executando ao invés
    // do boot parar inteiro. Isso elimina a categoria de bug "sync nunca
    // termina porque flushTombstones travou".
    const withTimeout = (promise, ms, label) => Promise.race([
      Promise.resolve(promise),
      new Promise((_, reject) => setTimeout(() => reject(new Error(`${label}: timeout ${ms}ms`)), ms)),
    ]);

    // Fase 30 — Limpa tombstones do remote ANTES de subir snapshot local,
    // pra evitar que comandas excluídas pelo admin re-entrem por outro path.
    try {
      await withTimeout(flushTombstones(cfg.deviceGroup), 3000, 'flushTombstones');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[FIREBASE SYNC] flushTombstones falhou (boot prossegue):', err?.message || err);
    }

    // Hotfix v6: Sincroniza com `__resetAt__` global do Firebase.
    // Se outro device fez Reset Total, este device limpa seu local ANTES
    // de subir snapshot — impede "comandas fantasmas" voltarem por re-push.
    let resetSynced = { wiped: false };
    try {
      resetSynced = await withTimeout(syncResetMarker(cfg.deviceGroup), 3000, 'syncResetMarker');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[FIREBASE SYNC] syncResetMarker falhou (boot prossegue, assumindo sem reset):', err?.message || err);
    }
    if (resetSynced.wiped) {
      // eslint-disable-next-line no-console
      console.log('[FIREBASE SYNC] Reset Total detectado de outro device — local limpo. Não subindo snapshot stale.');
      // v7.14 — Em vez de window.location.reload() automático (que quebra
      // sessões ativas e digitação em formulário), apenas emit um custom
      // event que componentes podem ouvir e responder com toast/redirect.
      try { window.dispatchEvent(new CustomEvent('kaven:resetDetected', { detail: { source: 'boot-sync' } })); } catch {}
    } else {
      // v7.11 — Aguarda a ingestão inicial de tombstones, então sobe o snapshot.
      // Substitui o setTimeout(1500ms) anterior, que era uma corrida cega.
      try {
        await withTimeout(tombstonesReady, 10000, 'tombstonesReady');
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[FIREBASE SYNC] tombstonesReady demorou >3s, subindo snapshot mesmo assim:', err?.message || err);
      }
      // pushLocalSnapshot já é fire-and-forget internamente, mas o helper
      // aplica timeout pra detectar travamentos graves.
      try {
        await withTimeout(Promise.resolve(pushLocalSnapshot(cfg.deviceGroup)), 5000, 'pushLocalSnapshot');
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[FIREBASE SYNC] pushLocalSnapshot timeout/erro (boot prossegue):', err?.message || err);
      }
      try {
        await withTimeout(Promise.resolve(pushSharedConfigSnapshot(cfg.deviceGroup)), 5000, 'pushSharedConfigSnapshot');
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[FIREBASE SYNC] pushSharedConfigSnapshot timeout/erro (boot prossegue):', err?.message || err);
      }
    }

    // Listener AO VIVO no __resetAt__: se outro device fizer Reset Total
    // enquanto este está aberto, detectamos em tempo real e auto-limpamos.
    try {
      const resetRef = ref(currentDb, `${cfg.deviceGroup}/__resetAt__`);
      const offReset = onValue(resetRef, async (snap) => {
        if (!snap.exists()) return;
        const remoteResetAt = Number(snap.val());
        const localResetAt = Number(localStorage.getItem('kaven_resetAt_local') || 0);
        if (remoteResetAt > localResetAt + 1000) {
          // v7.65 — CLIENTES (/delivery) nunca sofrem wipe local por Reset
          // Total de admin. Apenas atualiza o marker e deixa a mesclagem
          // incremental (listeners + filtro anti-zumbi) atualizar item a item.
          const isDeliveryClient = window.location.pathname.startsWith('/delivery');
          // eslint-disable-next-line no-console
          console.warn(`[FIREBASE SYNC] Reset Total recebido em tempo real — ${isDeliveryClient ? 'cliente /delivery: só marker, sem wipe' : 'limpando local'}.`);
          localStorage.setItem('kaven_resetAt_local', String(remoteResetAt));
          pendingWrites.clear();
          mergedVersions.clear();
          resolvedConflicts.clear();
          if (!isDeliveryClient) {
            try {
              const { resetDatabase } = await import('@/db');
              await resetDatabase();
            } catch {}
            // v7.14 — Sem reload automático. UI deve ouvir o event e reagir.
            // v7.65 — Suprimido em /delivery: cliente nunca vê "banco resetado".
            try { window.dispatchEvent(new CustomEvent('kaven:resetDetected', { detail: { source: 'live-listener' } })); } catch {}
          }
        }
      });
      // v7.70 — onValue retorna a função unsubscribe; `off(ref,'value',unsubFn)`
      // NÃO removia nada (unsubFn não é o callback registrado) → leak.
      tableListeners['__resetAt__'] = offReset;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[FIREBASE SYNC] Listener __resetAt__ falhou:', err?.message || err);
    }

    // eslint-disable-next-line no-console
    console.log('[FIREBASE SYNC] Inicializado — projectId:', cfg.projectId, 'group:', cfg.deviceGroup, 'uid:', currentUid);
    // v7.84 — registro de presença + escuta de revogação remota
    try { startDevicePresence(cfg.deviceGroup); } catch {}
    emit();
    return { ok: true, uid: currentUid };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[FIREBASE SYNC] Falha ao inicializar:', err);
    await teardownFirebaseSync();
    return { ok: false, reason: 'init_error', error: String(err?.message || err) };
  }
}

export async function teardownFirebaseSync() {
  if (connectionListener) { connectionListener(); connectionListener = null; }
  if (authUnsub) { authUnsub(); authUnsub = null; }
  for (const table of Object.keys(tableListeners)) {
    try { tableListeners[table](); } catch {}
  }
  tableListeners = {};
  if (currentApp) {
    try { await deleteApp(currentApp); } catch {}
  }
  currentApp = null;
  currentDb = null;
  currentAuth = null;
  currentUid = null;
  isConnected = false;
  isAuthenticated = false;
  remoteReceivedFlag = false;
  lastSyncMs.menuItems = { push: 0, receive: 0 };
  lastSyncMs.config = { push: 0, receive: 0 };
  mergedVersions.clear();
  resolvedConflicts.clear();
  // Hotfix v6: reseta o gate de auth para o próximo init começar limpo.
  resetAuthReady();
  emit();
}

function attachTableListener(deviceGroup, table) {
  const path = `${deviceGroup}/${table}`;
  const r = ref(currentDb, path);
  const unsub = onValue(r, async (snap) => {
    // v7.73 — Guard baseado APENAS no estado real do DB (db.isOpen()).
    // O flag isDbShuttingDown foi removido daqui: um flag preso silenciava
    // KDS/Entregador pra sempre. Se o Dexie está aberto, o listener escreve.
    if (!db.isOpen()) return;
    try {
      const val = snap.val();
      remoteReceivedFlag = true;
      if (lastSyncMs[table]) markSync(table, 'receive');
      else emit();
      await applyRemoteTableValue(deviceGroup, table, val);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[FIREBASE SYNC] listener ${table} catch:`, err?.message || err);
    }
  }, (err) => {
    // Hotfix v4: PERMISSION_DENIED não é fatal. O app continua funcionando
    // 100% offline-first. Só registramos no console como aviso e seguimos.
    const msg = String(err?.message || err);
    const isPermDenied = /PERMISSION_DENIED|permission_denied|denied/i.test(msg);
    if (isPermDenied) {
      // eslint-disable-next-line no-console
      console.warn(`[FIREBASE SYNC] listener ${table}: PERMISSION_DENIED — app segue offline-first. Verifique as regras do RTDB.`);
    } else {
      // eslint-disable-next-line no-console
      console.warn(`[FIREBASE SYNC] listener ${table} erro:`, err);
    }
  });
  // v7.70 — usa a função unsubscribe retornada pelo onValue (remove só este
  // callback). `off(r)` derrubaria TODOS os listeners do ref, mascarando bugs.
  tableListeners[table] = unsub;
}

/**
 * v8.06 — Merge de snapshot remoto EXTRAÍDO do onValue para que o pull REST
 * (restPullTables) use EXATAMENTE o mesmo caminho crítico: tombstones, LWW,
 * trava de status terminal, identidade kdsQueue por orderId, normalizações.
 * Recebe o JSON cru do nó da tabela; retorna nº de mudanças aplicadas.
 */
async function applyRemoteTableValue(deviceGroup, table, val) {
  if (!db.isOpen()) return 0;
  activeListenerWrites += 1;
  let appliedChanges = 0;
  try {
      if (!val || typeof val !== 'object') return appliedChanges;

      // Garante que a tabela existe no schema local antes de mexer
      if (!db.tables || !db.tables.some((t) => t.name === table)) return appliedChanges;

      // Fase 30 — Tombstones genéricos por tabela. Carrega 1x antes de
      // iterar (mais barato que ler config N vezes). Aplica para TODAS as
      // tabelas sincronizadas (commands, deliveryOrders, kdsQueue).
      // v7.71 — agora com timestamp: tombstone antigo NÃO mata registro novo
      // que reutilizou o mesmo id após Reset Total (auto-increment reinicia).
      const tombstoneTimes = await getTombstoneTimes(table);
      // v7.46 — Para tabelas com stableId (menuItems), também carrega o
      // set de stableIds tombstoned. Um item remoto cujo stableId foi
      // marcado como deletado em qualquer device é REJEITADO aqui,
      // independente do local-id que ele tenha neste device.
      const useStableTombstones = STABLE_ID_TABLES.includes(table);
      const stableTombstoneIds = useStableTombstones
        ? await getTombstonedStableIds(table)
        : null;

      // Hotfix v6.1 — Filtro anti-zumbi por __resetAt__.
      // Qualquer registro com createdAt ANTERIOR ao último Reset Total global
      // é considerado lixo (vazou de um device offline antigo que republicou
      // ao reconectar). Ignora E remove do Firebase imediatamente.
      const localResetAt = Number(localStorage.getItem('kaven_resetAt_local') || 0);

      for (const [idStr, payload] of Object.entries(val)) {
        // v7.69 — DB fechou no meio da iteração (reset/versionchange em
        // outra parte do app): aborta o merge em vez de espancar o Dexie
        // fechado com dezenas de put() que falhariam.
        if (!db.isOpen()) break;
        if (!payload || typeof payload !== 'object') continue;
        const remote = payload;
        const localId = Number(idStr);
        if (!Number.isFinite(localId) || localId <= 0) continue;

        // Fase 30 — Se o item foi tombstoned (excluído pelo admin), força
        // remoção remota e NUNCA aplica localmente. Previne ressurreição
        // após relogin/sync de outro device para qualquer tabela protegida.
        // v7.46 — Checa stableId PRIMEIRO (autoritativo cross-device).
        // v7.71 — Comparação temporal: tombstone só mata se for MAIS NOVO
        // que o createdAt do registro (ids reutilizados pós-reset sobrevivem).
        const remoteCreatedAt = new Date(remote.createdAt || remote.updatedAt || 0).getTime();
        const remoteStableId = useStableTombstones ? remote.stableId : null;
        const isStableTombstoned = remoteStableId && stableTombstoneIds.has(remoteStableId);
        const tombAt = tombstoneTimes.get(localId);
        if (isStableTombstoned || (tombAt !== undefined && tombstoneKillsRecord(tombAt, remoteCreatedAt))) {
          try {
            await remove(ref(currentDb, `${deviceGroup}/${table}/${localId}`));
          } catch {}
          try { await db.table(table).delete(localId); } catch {}
          // eslint-disable-next-line no-console
          if (isStableTombstoned) console.log(`[FBSYNC] ${table} stableId=${remoteStableId} bloqueado por tombstone — não ressuscitado.`);
          continue;
        }
        if (tombAt !== undefined) {
          // v7.71 — Tombstone STALE (registro é mais novo que a exclusão do
          // id antigo). Limpa local + remoto pra não reprocessar toda hora.
          // eslint-disable-next-line no-console
          console.log(`[FBSYNC] ${table}#${localId}: tombstone stale removido (registro novo reutilizou o id).`);
          try { await removeTombstone(table, localId); } catch {}
          try { await remove(ref(currentDb, `${deviceGroup}/__tombstones__/${table}/${localId}`)); } catch {}
          tombstoneTimes.delete(localId);
        }

        // Hotfix v6.1 — registro órfão de antes do último Reset Total.
        // Tolera 5s de skew (devices podem ter relógios levemente fora).
        const remoteCreatedAtMs = remoteCreatedAt;
        if (localResetAt > 0 && remoteCreatedAtMs > 0 && remoteCreatedAtMs < localResetAt - 5000) {
          // eslint-disable-next-line no-console
          console.warn(`[FIREBASE SYNC] zumbi descartado ${table}#${localId} (criado em ${new Date(remoteCreatedAt).toISOString()}, reset em ${new Date(localResetAt).toISOString()})`);
          try {
            await remove(ref(currentDb, `${deviceGroup}/${table}/${localId}`));
          } catch {}
          continue;
        }

        // Idempotência: se já mergeamos essa exata versão remota neste boot,
        // skipa (impede loop de re-processamento quando onValue dispara
        // múltiplas vezes ou listener é recriado).
        const versionKey = `${table}#${localId}`;
        const remoteVersion = remote.updatedAt || remote.createdAt || '';
        if (remoteVersion && mergedVersions.get(versionKey) === remoteVersion) {
          continue;
        }

        const local = await db.table(table).get(localId).catch(() => null);
        const remoteTs = new Date(remote.updatedAt || remote.createdAt || 0).getTime();
        const localTs = local ? new Date(local.updatedAt || local.createdAt || 0).getTime() : 0;

        // v7.84 (#207625) — kdsQueue: a identidade logica de um ticket de
        // cozinha e o `orderId` (numero do pedido), NAO o id auto-increment
        // local do Dexie. Dois aparelhos atribuem ids locais DIFERENTES ao
        // MESMO pedido (e o MESMO id local a pedidos diferentes), entao o LWW
        // por id abaixo aplicava a transicao de status na linha errada, criava
        // ticket duplicado, ou deixava um pedido preso porque o id remoto
        // colidia com uma linha local nao-relacionada. Resolve pelo orderId
        // ANTES do caminho por id (indice `orderId` ja existe no schema).
        if (table === 'kdsQueue' && remote.orderId !== undefined && remote.orderId !== null && remote.orderId !== '') {
          const byOrder = await db.kdsQueue.where('orderId').equals(remote.orderId).first().catch(() => null);
          if (byOrder) {
            // Mesmo ticket logico ja existe localmente (possivelmente com OUTRO
            // id local). Aplica LWW de status na PROPRIA linha dele — nunca
            // cria duplicata em `localId`. Guardado por applyingRemoteSync pra
            // nao re-publicar no Firebase (evita id/no divergente e fantasmas).
            const byOrderTs = new Date(byOrder.updatedAt || byOrder.createdAt || 0).getTime();
            if (remoteTs > byOrderTs + 1000) {
              applyingRemoteSyncDepth += 1;
              try {
                await db.kdsQueue.update(byOrder.id, {
                  status: (remote.status !== undefined && remote.status !== null) ? remote.status : byOrder.status,
                  readyAt: remote.readyAt !== undefined ? remote.readyAt : byOrder.readyAt,
                  source: remote.source || byOrder.source,
                  updatedAt: remote.updatedAt || new Date().toISOString(),
                });
              } finally {
                applyingRemoteSyncDepth -= 1;
              }
            }
            if (remoteVersion) mergedVersions.set(versionKey, remoteVersion);
            continue;
          }
          // byOrder ausente = ticket novo pra este aparelho. Cai no caminho por
          // id abaixo: se o id remoto estiver livre insere; se estiver ocupado
          // por uma linha MAIS ANTIGA (fantasma), o LWW sobrescreve e o pedido
          // aparece. (Colisao com linha NEWER de outro pedido e rara e so tem
          // solucao correta com chave estavel/stableId — escalado pra eng.)
        }

        // LWW (last-write-wins) + tolera clock skew pequeno (1s)
        // v7.84 — TRAVA DE CICLO DE VIDA: pedido em status TERMINAL
        // (delivered/cancelled) NUNCA regride para status ativo, mesmo que
        // um device com relógio adiantado/reconexão tardia empurre payload
        // "mais novo". Impede pedido entregue de voltar pra fila da Cozinha.
        if (
          (table === 'deliveryOrders' || table === 'kdsQueue') &&
          local &&
          (String(local.status) === 'delivered' || String(local.status) === 'cancelled') &&
          remote.status && String(remote.status) !== 'delivered' && String(remote.status) !== 'cancelled'
        ) {
          if (remoteVersion) mergedVersions.set(versionKey, remoteVersion);
          continue;
        }
        if (!local || remoteTs > localTs + 1000) {
          try {
            // Limpa campos internos (id) para evitar conflito
            const { id: _skip, ...rest } = remote;
            // Hotfix v7.3 — Firebase RTDB serializa arrays como objetos
            // `{0: 'a', 1: 'b'}`. Renormalizamos campos que JSConsumers
            // tratam como array (presets, tags) para não quebrar
            // .includes/.find no React.
            // v7.12 — normalização expandida para TODOS os campos que
            // possam chegar como objetos do Firebase.
            const normalized = { ...rest, id: localId };
            for (const arrKey of ['presets', 'tags', 'items', 'extras', 'addons', 'observations', 'options', 'modifiers', 'categories', 'photos', 'images']) {
              const v = normalized[arrKey];
              if (v && typeof v === 'object' && !Array.isArray(v)) {
                normalized[arrKey] = Object.values(v);
              }
            }
            // v7.12 — Validação defensiva de categoria para menuItems.
            // Garante que itens remotos com categoria fora das 5 oficiais
            // sejam recategorizados ANTES de chegar no Dexie (evita
            // re-render quebrado e tela em branco no Delivery/Cardápio).
            if (table === 'menuItems') {
              // v7.51 — REJEITA itens fantasma na fonte. Mesmo que estejam
              // no Firebase RTDB (por seed antigo, device com bug), nunca
              // entram no Dexie local. Adicionalmente, força exclusão do
              // node Firebase para que o resto da frota também limpe.
              try {
                const { isGhostMenuItem } = await import('@/lib/ghostMenuBlacklist');
                if (isGhostMenuItem(normalized)) {
                  // eslint-disable-next-line no-console
                  console.warn(`[FBSYNC v7.51] menuItem fantasma rejeitado: ${normalized.name} (id=${localId}) — apagando do Firebase`);
                  try { await remove(ref(currentDb, `${deviceGroup}/${table}/${localId}`)); } catch {}
                  if (remoteVersion) mergedVersions.set(versionKey, remoteVersion);
                  continue;
                }
              } catch {}
              const VALID_CATEGORIES = new Set(['Executivo Premium', 'Churrasco Box', 'Massas & Cremosos', 'Família', 'Bebidas']);
              if (!VALID_CATEGORIES.has(normalized.category)) {
                normalized.category = 'Executivo Premium';
              }
              if (!Array.isArray(normalized.presets) || normalized.presets.length === 0) {
                normalized.presets = ['Padrão'];
              }
              if (!Array.isArray(normalized.tags)) {
                normalized.tags = [];
              }
              if (normalized.available === undefined || normalized.available === null) {
                normalized.available = true;
              }
              if (typeof normalized.servings === 'number' && normalized.servings <= 0) {
                normalized.servings = null;
              }
            }
            applyingRemoteSyncDepth += 1;
            try {
              await db.table(table).put(normalized);
              appliedChanges += 1;
            } finally {
              applyingRemoteSyncDepth -= 1;
            }
            if (remoteVersion) mergedVersions.set(versionKey, remoteVersion);
          } catch (e) {
            // Resolução de conflito de unique index (commands.&number).
            if (e?.name === 'ConstraintError' && remote?.number != null && table === 'commands') {
              const conflictKey = `${table}#number=${remote.number}`;
              if (resolvedConflicts.has(conflictKey)) {
                // Já resolvemos esse number neste boot — só registra como
                // mergeado pra parar de tentar e evita logspam.
                if (remoteVersion) mergedVersions.set(versionKey, remoteVersion);
                continue;
              }
              try {
                const dup = await db.table(table).where('number').equals(Number(remote.number)).first();
                if (dup && dup.id !== localId) {
                  const dupTs = new Date(dup.updatedAt || dup.createdAt || 0).getTime();
                  if (remoteTs > dupTs + 1000) {
                    // Remote é mais novo: remove o dup local, aplica remote
                    // eslint-disable-next-line no-console
                    console.warn(`[FIREBASE SYNC] conflito number=${remote.number}: remote mais novo, removendo dup local id=${dup.id}`);
                    await db.table(table).delete(dup.id);
                    const { id: _s2, ...rest2 } = remote;
                    await db.table(table).put({ ...rest2, id: localId });
                    // Limpa o id antigo no remote pra parar de pingar
                    try { await remove(ref(currentDb, `${deviceGroup}/${table}/${dup.id}`)); } catch {}
                  } else {
                    // Local é mais novo: ignora remote E limpa o id remoto stale
                    // eslint-disable-next-line no-console
                    console.warn(`[FIREBASE SYNC] conflito number=${remote.number}: local mais novo, removendo registro remoto stale id=${localId}`);
                    try { await remove(ref(currentDb, `${deviceGroup}/${table}/${localId}`)); } catch {}
                  }
                  resolvedConflicts.add(conflictKey);
                }
                if (remoteVersion) mergedVersions.set(versionKey, remoteVersion);
              } catch (e2) {
                // eslint-disable-next-line no-console
                console.warn(`[FIREBASE SYNC] conflict-resolution falhou ${table}#${localId}:`, e2?.message || e2);
              }
            } else {
              // eslint-disable-next-line no-console
              console.warn(`[FIREBASE SYNC] merge fail ${table}#${localId}`, e?.message || e);
            }
          }
        } else if (remoteVersion) {
          // Local já está em dia — só marca como mergeado pra economizar
          mergedVersions.set(versionKey, remoteVersion);
        }
      }
      // v7.73 — Tempo real na UI: notifica as telas (KDS/Entregador) que
      // dados desta tabela mudaram — elas recarregam na hora, sem F5 e sem
      // esperar o próximo tick do polling.
      if (appliedChanges > 0) {
        try { window.dispatchEvent(new CustomEvent('kaven:remote-merge', { detail: { table, count: appliedChanges } })); } catch {}
      }
  } finally {
    activeListenerWrites -= 1;
  }
  return appliedChanges;
}

/**
 * v8.06 — PULL REST de recuperação ativa: busca o nó da tabela por HTTP puro
 * (fetch), BYPASSANDO o socket do SDK (que morre em hibernação e não avisa),
 * e aplica pelo MESMO merge do listener. É o fallback quando o app está em
 * "Sincronização Parada" — puxa os dados reais sem depender de reconexão.
 */
export async function restPullTables(tables = ['kdsQueue', 'deliveryOrders']) {
  try {
    if (!db.isOpen()) return false;
    const cfg = await getFirebaseConfig();
    if (!cfg.enabled || !cfg.deviceGroup || !cfg.databaseURL) return false;
    let token = '';
    try { token = (await currentAuth?.currentUser?.getIdToken()) || ''; } catch {}
    if (!token) return false;
    const base = String(cfg.databaseURL).replace(/\/+$/, '');
    let pulled = false;
    for (const t of tables) {
      try {
        const controller = new AbortController();
        const tm = setTimeout(() => controller.abort(), 8000);
        const resp = await fetch(`${base}/${cfg.deviceGroup}/${t}.json?auth=${encodeURIComponent(token)}`, { cache: 'no-store', signal: controller.signal });
        clearTimeout(tm);
        if (!resp.ok) continue;
        const val = await resp.json();
        remoteReceivedFlag = true;
        if (lastSyncMs[t]) markSync(t, 'receive');
        await applyRemoteTableValue(cfg.deviceGroup, t, val);
        pulled = true;
      } catch { /* tabela falhou: tenta as demais */ }
    }
    return pulled;
  } catch {
    return false;
  }
}

/**
 * v7.81 — Resync de segurança: religa os listeners das tabelas indicadas.
 * O onValue re-anexado entrega imediatamente o snapshot COMPLETO do nó,
 * que passa pelo MESMO caminho de merge do attachTableListener (tombstones,
 * LWW, mergedVersions) — zero duplicação da lógica sensível. Se um listener
 * morreu silenciosamente (socket, erro, DB fechado durante merge), isso o
 * ressuscita e recupera qualquer registro perdido. Usado pelo watchdog de
 * 15s da Cozinha (KDS).
 */
export async function resyncTablesFromRemote(tables = ['kdsQueue', 'deliveryOrders']) {
  try {
    if (!currentDb || !db.isOpen()) return false;
    const cfg = await getFirebaseConfig();
    if (!cfg.enabled || !cfg.deviceGroup) return false;
    for (const t of tables) {
      try {
        if (tableListeners[t]) {
          tableListeners[t]();
          delete tableListeners[t];
        }
        attachTableListener(cfg.deviceGroup, t);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(`[FIREBASE SYNC] resync ${t} falhou:`, e?.message || e);
      }
    }
    // v7.82 — Religa também a PONTE backendOrders (pedidos pagos promovidos
    // pelo servidor). Falha transitória no boot (currentDb null durante
    // re-init — erro '_checkNotDeleted') deixava pedidos da ponte órfãos até
    // o F5. O re-attach entrega o snapshot completo e reprocessa os que
    // faltaram (claims/dedup impedem duplicatas).
    try {
      if (tableListeners['__backendOrders__']) {
        tableListeners['__backendOrders__']();
        delete tableListeners['__backendOrders__'];
      }
      attachBackendOrdersListener(cfg.deviceGroup);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[FIREBASE SYNC] resync backendOrders falhou:', e?.message || e);
    }
    return true;
  } catch {
    return false;
  }
}

// ============ v7.84 — Presença e revogação remota de dispositivos ============
// Cada device se registra em `{group}/__devices__/{deviceId}` (heartbeat 60s)
// e escuta o campo `revokedAt`. Quando o admin revoga: o device limpa a fila
// de pendingWrites (memória + localStorage), derruba o sync, apaga o próprio
// registro e desloga — deixa de ser vetor de injeção de filas corrompidas.
const DEVICE_ID_KEY = 'kaven_device_id';

// v7.85 — ID de dispositivo DETERMINÍSTICO (fingerprint estável do
// navegador/hardware). O id aleatório anterior gerava um device "novo" a
// cada limpeza de cache/aba anônima, duplicando o aparelho no Painel de
// Dispositivos. Com fingerprint, o MESMO navegador sempre regenera o MESMO
// id, mesmo depois de limpar o storage. Redundância: localStorage + cookie
// (1 ano). IDs antigos migram e o node órfão é removido no registro.
function computeDeviceFingerprint() {
  try {
    const n = navigator;
    const s = window.screen || {};
    const parts = [
      n.userAgent || '',
      n.platform || '',
      n.language || '',
      (Intl.DateTimeFormat().resolvedOptions() || {}).timeZone || '',
      `${s.width || 0}x${s.height || 0}x${s.colorDepth || 0}`,
      String(n.hardwareConcurrency || 0),
      String(n.deviceMemory || 0),
      String(n.maxTouchPoints || 0),
    ].join('|');
    let h1 = 0x811c9dc5;
    let h2 = 0x12345679;
    for (let i = 0; i < parts.length; i++) {
      const c = parts.charCodeAt(i);
      h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
      h2 = (Math.imul(h2 + c, 0x85ebca6b) ^ (h2 >>> 13)) >>> 0;
    }
    return `dev-${h1.toString(36)}${h2.toString(36)}`;
  } catch {
    return null;
  }
}

export function getLocalDeviceId() {
  // 1) Chave já existente é IMUTÁVEL: nunca sobrescrita nem recriada em
  //    novas abas (localStorage é compartilhado por aba) ou limpezas
  //    parciais (cookie de 1 ano como redundância).
  let stored = null;
  try { stored = localStorage.getItem(DEVICE_ID_KEY); } catch {}
  if (!stored) {
    try {
      const m = document.cookie.match(/(?:^|;\s*)kaven_device_id=([^;]+)/);
      if (m) stored = decodeURIComponent(m[1]);
    } catch {}
  }
  if (stored) {
    try { localStorage.setItem(DEVICE_ID_KEY, stored); } catch {}
    try {
      document.cookie = `kaven_device_id=${encodeURIComponent(stored)};path=/;max-age=31536000;SameSite=Lax`;
    } catch {}
    return stored;
  }
  // 2) Primeira vez neste aparelho: gera id DETERMINÍSTICO (fingerprint) —
  //    mesmo após limpeza TOTAL do storage, o mesmo navegador regenera o
  //    MESMO id, evitando sessões duplicadas no Painel de Dispositivos.
  const id = computeDeviceFingerprint() || `dev-mem-${Math.random().toString(36).slice(2, 10)}`;
  try { localStorage.setItem(DEVICE_ID_KEY, id); } catch {}
  try {
    document.cookie = `kaven_device_id=${encodeURIComponent(id)};path=/;max-age=31536000;SameSite=Lax`;
  } catch {}
  return id;
}

let presenceStarted = false;
let presenceHeartbeatId = null;

// v7.91 — Poda automática de FANTASMAS: nós de dispositivos sem heartbeat
// há mais de 48h são registros órfãos (IDs aleatórios pré-v7.85, storages
// limpos, navegadores antigos substituídos). São removidos no registro de
// presença — a lista do painel se mantém limpa sozinha, sem intervenção.
// NÃO altera o id local, filas nem qualquer outra lógica de sync.
const STALE_DEVICE_MS = 48 * 3600 * 1000;

async function pruneStaleDeviceNodes(deviceGroup, selfId) {
  try {
    if (!currentDb || !isAuthenticated) return 0;
    const snap = await get(ref(currentDb, `${deviceGroup}/__devices__`));
    const val = snap.exists() ? snap.val() : null;
    if (!val || typeof val !== 'object') return 0;
    const cutoff = Date.now() - STALE_DEVICE_MS;
    let pruned = 0;
    for (const [key, d] of Object.entries(val)) {
      if (key === selfId) continue;
      const seen = (d && d.lastSeenAt) || 0;
      if (seen < cutoff) {
        try {
          await remove(ref(currentDb, `${deviceGroup}/__devices__/${key}`));
          pruned += 1;
        } catch {}
      }
    }
    if (pruned > 0) {
      // eslint-disable-next-line no-console
      console.log(`[FIREBASE SYNC] ${pruned} registro(s) fantasma de dispositivo removido(s) (sem heartbeat há 48h+)`);
    }
    return pruned;
  } catch {
    return 0;
  }
}

function startDevicePresence(deviceGroup) {
  if (presenceStarted || !currentDb) return;
  // Rotas públicas (clientes) não registram presença — o painel lista só
  // dispositivos da OPERAÇÃO (cozinha, entregador, caixa, admin).
  const path0 = window.location.pathname;
  if (path0.startsWith('/delivery') || path0.startsWith('/rastreio')) return;
  presenceStarted = true;
  const deviceId = getLocalDeviceId();
  const nodePath = `${deviceGroup}/__devices__/${deviceId}`;
  const beat = async () => {
    try {
      if (!currentDb || !isAuthenticated) return;
      await refreshDeviceNicknameCache();
      // v8.01 — versão do bundle rodando neste aparelho (painel de devices)
      let appVersion = null;
      try {
        const { getCurrentBundleHash } = await import('@/lib/versionGuard');
        appVersion = getCurrentBundleHash();
      } catch {}
      await update(ref(currentDb, nodePath), {
        deviceId,
        nickname: cachedNickname || null,
        page: window.location.pathname,
        ua: (navigator.userAgent || '').slice(0, 90),
        queueSize: pendingWrites.size,
        appVersion,
        lastSeenAt: Date.now(),
      });
    } catch {}
  };
  // Primeiro registro zera revokedAt residual (revogação anterior já foi
  // cumprida — sem isso o device entraria em loop de revogação ao relogar).
  // Só ANEXA o listener de revogação depois do set(null) completar.
  (async () => {
    try {
      if (currentDb) await set(ref(currentDb, `${nodePath}/revokedAt`), null);
    } catch {}
    beat();
    attachRevokeListener();
    // v7.91 — Limpa fantasmas (48h+ sem heartbeat) uma vez por sessão
    pruneStaleDeviceNodes(deviceGroup, deviceId);
  })();
  presenceHeartbeatId = setInterval(beat, 60000);
  function attachRevokeListener() {
    if (!currentDb) return;
    try {
      onValue(ref(currentDb, `${nodePath}/revokedAt`), async (snap) => {
        const at = snap.val();
        if (!at) return;
        // eslint-disable-next-line no-console
        console.warn('[FIREBASE SYNC] Sessão REVOGADA pelo administrador — neutralizando device.');
        try { clearInterval(presenceHeartbeatId); } catch {}
        try {
          pendingWrites.clear();
          clearPersistedPendingWrites();
        } catch {}
        try { await remove(ref(currentDb, nodePath)); } catch {}
        try { await teardownFirebaseSync(); } catch {}
        try { sessionStorage.removeItem('kaven_operator'); } catch {}
        presenceStarted = false;
        const p = window.location.pathname;
        window.location.replace(p.startsWith('/delivery') || p.startsWith('/rastreio') ? p : '/login?revogado=1');
      });
    } catch {}
  }
}

export async function listActiveDevices() {
  if (!currentDb) return [];
  const cfg = await getFirebaseConfig();
  if (!cfg.enabled || !cfg.deviceGroup) return [];
  await waitForAuth();
  const snap = await get(ref(currentDb, `${cfg.deviceGroup}/__devices__`));
  const val = snap.exists() ? snap.val() : null;
  if (!val || typeof val !== 'object') return [];
  return Object.values(val).filter((d) => d && d.deviceId);
}

export async function revokeDeviceSession(deviceId) {
  if (!currentDb || !deviceId) return false;
  const cfg = await getFirebaseConfig();
  if (!cfg.deviceGroup) return false;
  await set(ref(currentDb, `${cfg.deviceGroup}/__devices__/${deviceId}/revokedAt`), Date.now());
  return true;
}

export async function removeDeviceRecord(deviceId) {
  if (!currentDb || !deviceId) return false;
  const cfg = await getFirebaseConfig();
  if (!cfg.deviceGroup) return false;
  await remove(ref(currentDb, `${cfg.deviceGroup}/__devices__/${deviceId}`));
  return true;
}

/**
 * v7.82 — Purga DEFINITIVA da ponte backend de um pedido no Firebase:
 * remove `backendOrders/{mpPaymentId}` (snapshot que poderia reintroduzir o
 * pedido) e grava `backendOrderClaims/{mpPaymentId}=true` (mesmo que o
 * backend re-escreva a ponte no futuro, nenhum device consegue reivindicar
 * e recriar o pedido). Chamado na exclusão pelo Admin e na finalização com
 * código de entrega.
 */
export async function purgeBackendOrderBridge(mpPaymentId) {
  if (!mpPaymentId || !currentDb) return false;
  try {
    const cfg = await getFirebaseConfig();
    if (!cfg.enabled || !cfg.deviceGroup) return false;
    await waitForAuth();
    if (!isAuthenticated) return false;
    try { await remove(ref(currentDb, `${cfg.deviceGroup}/backendOrders/${mpPaymentId}`)); } catch {}
    try { await set(ref(currentDb, `${cfg.deviceGroup}/backendOrderClaims/${mpPaymentId}`), true); } catch {}
    return true;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`[FIREBASE SYNC] purgeBackendOrderBridge falhou p/ ${mpPaymentId}:`, e?.message || e);
    return false;
  }
}

/**
 * TICKET 207625 - Re-religa a PONTE backendOrders (backend Mongo -> Firebase
 * -> Dexie kdsQueue/deliveryOrders). O backend escreve o pedido pago em
 * `{deviceGroup}/backendOrders/{id}` (server-side, autoritativo), mas quem
 * transforma isso em kdsQueue/deliveryOrders e um LISTENER de cliente
 * (attachBackendOrdersListener). Se o tablet da Cozinha estava em segundo
 * plano/adormecido quando o pedido chegou, esse listener nao processou -- e o
 * resync de kdsQueue/deliveryOrders NAO cobre backendOrders. Aqui religamos o
 * listener: o onValue re-dispara com o snapshot atual de backendOrders -> a
 * ponte re-processa (idempotente via backendOrderClaims + checagem de pedido
 * existente) -> cria os kdsQueue/deliveryOrders que faltavam.
 */
export async function resyncBackendOrders() {
  try {
    if (!currentDb || !db.isOpen()) return false;
    const cfg = await getFirebaseConfig();
    if (!cfg.enabled || !cfg.deviceGroup) return false;
    if (tableListeners['__backendOrders__']) {
      try { tableListeners['__backendOrders__'](); } catch {}
      delete tableListeners['__backendOrders__'];
    }
    attachBackendOrdersListener(cfg.deviceGroup);
    return true;
  } catch {
    return false;
  }
}

/**
 * Fase 30 — Ao conectar/autenticar, varre tombstones de TODAS as tabelas
 * sincronizadas e força a remoção dos itens correspondentes no Firebase.
 * Resolve casos de exclusão offline ou de outro device.
 */
/**
 * Hotfix v6 — Reset Total GLOBAL.
 *
 * Armazena `${deviceGroup}/__resetAt__` no Firebase quando o Admin clica
 * "Apagar TUDO". Outros devices, ao bootar (ou recuperar conexão), comparam
 * com o `kaven_resetAt_local` no localStorage. Se o remoto for mais recente:
 *   1) Limpa Dexie local (`resetDatabase`) ANTES de subir qualquer snapshot.
 *   2) Persiste `kaven_resetAt_local = remoteResetAt`.
 *
 * Resultado: o reset agora é "global", não "deste device". Comandas-fantasma
 * vindas de outros celulares com dados locais antigos NÃO conseguem voltar
 * porque o device delas vai detectar o marker e se autolimpar.
 */
async function syncResetMarker(deviceGroup) {
  try {
    const snap = await get(ref(currentDb, `${deviceGroup}/__resetAt__`));
    const remoteResetAt = snap.exists() ? Number(snap.val()) : 0;
    if (!remoteResetAt) return { wiped: false };
    const localResetAt = Number(localStorage.getItem('kaven_resetAt_local') || 0);
    if (remoteResetAt > localResetAt) {
      // v7.82 — Device NOVO (sem marker local): não há nada stale pra
      // limpar — o wipe aqui destruía o boot de TODO navegador/tablet novo
      // ("Reset Total detectado" + resetDatabase + teardown no meio do init
      // → sync morto até o F5). Apenas ADOTA o marker; o filtro anti-zumbi
      // (createdAt < resetAt) já bloqueia registros pré-reset nos merges.
      if (!localResetAt) {
        localStorage.setItem('kaven_resetAt_local', String(remoteResetAt));
        pendingWrites.clear();
        mergedVersions.clear();
        resolvedConflicts.clear();
        // eslint-disable-next-line no-console
        console.log('[FIREBASE SYNC] Device novo: marker __resetAt__ adotado sem wipe.');
        return { wiped: false, resetAt: remoteResetAt };
      }
      // v7.65 — Clientes /delivery NÃO sofrem wipe: só atualizam o marker e
      // deixam a mesclagem incremental (anti-zumbi) cuidar das atualizações.
      if (window.location.pathname.startsWith('/delivery')) {
        localStorage.setItem('kaven_resetAt_local', String(remoteResetAt));
        pendingWrites.clear();
        mergedVersions.clear();
        resolvedConflicts.clear();
        return { wiped: false, resetAt: remoteResetAt };
      }
      // Outro device fez Reset Total APÓS nosso último sync. Limpa local.
      const { resetDatabase } = await import('@/db');
      await resetDatabase();
      localStorage.setItem('kaven_resetAt_local', String(remoteResetAt));
      // Limpa caches em memória pra não republicar nada antigo
      pendingWrites.clear();
      mergedVersions.clear();
      resolvedConflicts.clear();
      return { wiped: true, resetAt: remoteResetAt };
    }
    // Local já está em dia.
    localStorage.setItem('kaven_resetAt_local', String(remoteResetAt));
    return { wiped: false };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[FIREBASE SYNC] syncResetMarker erro (ignorado):', err?.message || err);
    return { wiped: false };
  }
}

async function flushTombstones(deviceGroup) {
  // Hotfix v6.3 — paralelo + multi-path null update (1 round-trip por tabela).
  await Promise.all(SYNCED_TABLES.map(async (table) => {
    try {
      const list = await getTombstones(table);
      if (!list.length) return;
      const updates = {};
      for (const t of list) {
        const id = Number(t?.id);
        if (!Number.isFinite(id) || id <= 0) continue;
        updates[`${deviceGroup}/${table}/${id}`] = null; // null no multi-path = delete
      }
      if (Object.keys(updates).length === 0) return;
      try {
        await update(ref(currentDb), updates);
        // eslint-disable-next-line no-console
        console.log(`[FIREBASE SYNC] Flushed ${list.length} tombstone(s) in ${table}`);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(`[FIREBASE SYNC] flushTombstones bulk ${table} fail:`, e?.message || e);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[FIREBASE SYNC] flushTombstones ${table} erro:`, err?.message || err);
    }
  }));
}

/**
 * Remove recursivamente chaves com valor `undefined` de um objeto/array.
 * O Firebase rejeita writes com `undefined` em qualquer nível, gerando
 * "set failed: value argument contains undefined in property '...'".
 * Mantém `null` (que o Firebase aceita = delete).
 */
function stripUndefined(value) {
  if (Array.isArray(value)) {
    return value.map(stripUndefined).filter((v) => v !== undefined);
  }
  if (value && typeof value === 'object') {
    const out = {};
    for (const k of Object.keys(value)) {
      const v = stripUndefined(value[k]);
      if (v !== undefined) out[k] = v;
    }
    return out;
  }
  return value;
}

/**
 * Fila de writes pendentes quando o push é chamado ANTES de o auth completar
 * (caso normal: hooks do Dexie disparam logo no boot, mas signInAnonymously
 * pode demorar 1-3s). Sem isso, todos os writes feitos nesse intervalo seriam
 * descartados silenciosamente.
 *
 * Usa Map com chave `${table}#${id}` pra deduplicar: se a mesma comanda for
 * atualizada 5x antes do auth, só o último estado vai pro Firebase.
 */
const pendingWrites = new Map();
let flushingQueue = false;

// v7.82 — Persistencia do pendingWrites em localStorage (ticket #207625).
// Antes, esta fila era um Map so-em-memoria: se o push explicito de
// markReady() falhasse (rede instavel, RTDB timeout) e a pagina fosse
// atualizada/fechada ANTES do proximo flush de 15s, a fila inteira era
// perdida silenciosamente -- o Firebase (fonte de verdade compartilhada)
// ficava PARA SEMPRE com o status antigo, e qualquer outro device (ou o
// mesmo device apos F5, ou um device novo sem historico local) via o
// pedido "voltar" para o status anterior. Persistimos a fila a cada
// mutacao e hidratamos no boot ANTES de qualquer push, para que um write
// pendente sobreviva a um refresh/fechamento de aba.
const PENDING_WRITES_STORAGE_KEY = 'kaven_pendingWrites_v1';

function persistPendingWrites() {
  try {
    localStorage.setItem(PENDING_WRITES_STORAGE_KEY, JSON.stringify(Array.from(pendingWrites.entries())));
  } catch {}
}

function clearPersistedPendingWrites() {
  try { localStorage.removeItem(PENDING_WRITES_STORAGE_KEY); } catch {}
}

function hydratePendingWritesFromStorage() {
  try {
    const raw = localStorage.getItem(PENDING_WRITES_STORAGE_KEY);
    if (!raw) return;
    const entries = JSON.parse(raw);
    if (Array.isArray(entries)) {
      const nowMs = Date.now();
      let restored = 0;
      for (const [key, val] of entries) {
        // v7.83 — descarta writes com mais de 24h (lixo de sessões antigas
        // que ressuscitava registros excluídos ao ser re-hidratado).
        if (val?.ts && nowMs - val.ts > 24 * 3600 * 1000) continue;
        pendingWrites.set(key, val);
        restored += 1;
      }
      // eslint-disable-next-line no-console
      if (restored) console.log(`[FIREBASE SYNC] ${restored} write(s) pendente(s) recuperado(s) de antes do refresh/fechamento.`);
    }
  } catch {}
}
// Roda uma vez, no carregamento do modulo -- antes de qualquer push desta sessao.
hydratePendingWritesFromStorage();

/**
 * v7.47 — Snapshot do estado da fila e do flush em andamento. Usado pelo
 * `resetDatabase` em db.js pra esperar writes drenarem ANTES de deletar
 * o IndexedDB (sem isso, um write em vôo pode lançar DatabaseClosedError
 * e contaminar a próxima sessão).
 */
export function getPendingWritesStatus() {
  return { size: pendingWrites.size, flushing: flushingQueue };
}

/**
 * v7.47 — Abandona writes pendentes sem flush. Usado pelo resetDatabase
 * — depois de deletar o DB, qualquer write em vôo é lixo (referenciando
 * IDs do schema antigo).
 */
export function clearPendingWrites() {
  pendingWrites.clear();
  clearPersistedPendingWrites();
  flushingQueue = false;
}

async function flushPendingWrites() {
  if (flushingQueue) return;
  if (!isAuthenticated || !currentDb || pendingWrites.size === 0) return;
  flushingQueue = true;
  try {
    const cfg = await getFirebaseConfig();
    if (!cfg.enabled) return;
    const entries = Array.from(pendingWrites.entries());
    pendingWrites.clear();
    clearPersistedPendingWrites();

    // v7.83 — FILTRO ANTI-RESSURREIÇÃO (causa raiz dos "pedidos que voltam
    // sozinhos ~10min após a exclusão"): esta fila re-entregava payloads
    // ANTIGOS em bulk cru, SEM as guardas de tombstone/reset que o
    // pushToRemote ao vivo tem (Hotfix v7.6). Um device com fila persistida
    // (localStorage, v7.82) reconectava e re-publicava no Firebase raiz
    // registros que o admin tinha acabado de excluir. Agora, antes do bulk:
    //  1. Busca __tombstones__ remotos — se não conseguir ler, ADIA o flush
    //     (nunca entrega às cegas);
    //  2. Descarta writes de registros com tombstone válido (regra temporal);
    //  3. Descarta writes pré-Reset Total;
    //  4. Descarta writes com mais de 24h na fila (lixo de sessões antigas).
    let remoteTombs;
    try {
      const tsSnap = await get(ref(currentDb, `${cfg.deviceGroup}/__tombstones__`));
      remoteTombs = tsSnap.exists() ? (tsSnap.val() || {}) : {};
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[FIREBASE SYNC] flush adiado — sem visão dos tombstones:', err?.message || err);
      for (const [key, val] of entries) pendingWrites.set(key, val);
      persistPendingWrites();
      return;
    }
    const localResetAt = Number(localStorage.getItem('kaven_resetAt_local') || 0);
    const nowMs = Date.now();
    const kept = [];
    let dropped = 0;
    for (const entry of entries) {
      const w = entry[1] || {};
      const { table, id, payload, ts } = w;
      if (ts && nowMs - ts > 24 * 3600 * 1000) { dropped += 1; continue; }
      const createdMs = new Date(payload?.createdAt || payload?.updatedAt || 0).getTime() || 0;
      if (localResetAt > 0 && createdMs > 0 && createdMs < localResetAt - 5000) { dropped += 1; continue; }
      const tomb = remoteTombs?.[table]?.[id];
      if (tomb) {
        const src = tomb.deletedAt || tomb.at;
        const tombMs = src ? new Date(src).getTime() : Infinity;
        if (tombstoneKillsRecord(Number.isFinite(tombMs) ? tombMs : Infinity, createdMs || null)) {
          dropped += 1;
          continue;
        }
      }
      // v7.84 — Trava de ciclo de vida no FLUSH: write enfileirado com
      // status ATIVO nunca sobrescreve registro remoto já FINALIZADO
      // (delivered/cancelled) — reconexão tardia não reabre pedido.
      if (
        (table === 'deliveryOrders' || table === 'kdsQueue') &&
        payload?.status && payload.status !== 'delivered' && payload.status !== 'cancelled'
      ) {
        try {
          const stSnap = await get(ref(currentDb, `${cfg.deviceGroup}/${table}/${id}/status`));
          const remoteStatus = stSnap.exists() ? String(stSnap.val()) : null;
          if (remoteStatus === 'delivered' || remoteStatus === 'cancelled') {
            dropped += 1;
            continue;
          }
        } catch {}
      }
      kept.push(entry);
    }
    if (dropped > 0) {
      // eslint-disable-next-line no-console
      console.log(`[FIREBASE SYNC] flush: ${dropped} write(s) descartado(s) (tombstone/reset/expirado) — anti-ressurreição.`);
    }
    if (kept.length === 0) return;

    // Hotfix v6.3 — multi-path update: 1 round-trip pra todos os writes.
    const updates = {};
    for (const [, { table, id, payload }] of kept) {
      updates[`${cfg.deviceGroup}/${table}/${id}`] = payload;
    }
    try {
      await update(ref(currentDb), updates);
      // eslint-disable-next-line no-console
      console.log(`[FIREBASE SYNC] flush concluído: ${kept.length} write(s) entregue(s) em bulk`);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[FIREBASE SYNC] flush bulk falhou:', err?.message || err);
      // Re-enfileira pra próxima oportunidade. v7.82 -- persiste de novo, senão
      // um segundo refresh antes do próximo flush perderia a fila outra vez.
      for (const [key, val] of kept) pendingWrites.set(key, val);
      persistPendingWrites();
    }
  } finally {
    flushingQueue = false;
  }
}

let pendingWritesFlushIntervalStarted = false;
/**
 * v7.76 — flushPendingWrites só disparava em transição de auth
 * (false→true), que normalmente só acontece uma vez por sessão (logo
 * no boot). Um write que ficasse enfileirado depois disso (ex: falha
 * transitória de rede numa tela de cozinha aberta o dia todo) nunca
 * ganhava uma segunda chance de reenvio. Agora tenta drenar a fila a
 * cada 15s, independente de mudança de estado de auth. Idempotente e
 * guardado por flag — chamar mais de uma vez não cria intervalos duplicados.
 */
function startPendingWritesFlushInterval() {
  if (pendingWritesFlushIntervalStarted) return;
  pendingWritesFlushIntervalStarted = true;
  setInterval(() => { flushPendingWrites(); }, 15000);
}

/**
 * Empurra um documento local para o RTDB. Chamado pelos hooks de mutação do Dexie.
 * Se o auth ainda não terminou, ENFILEIRA pra entregar quando autenticar.
 */
export async function pushToRemote(table, id, data) {
  if (!SYNCED_TABLES.includes(table)) return { ok: false, reason: 'not_synced_table' };
  const cfg = await getFirebaseConfig();
  // Hotfix v6.1 — não pushar registros mais antigos que o último Reset Total.
  // Bloqueia republicação de comandas zumbi por hooks do Dexie tardios.
  const localResetAt = Number(localStorage.getItem('kaven_resetAt_local') || 0);
  const rowCreatedAt = new Date(data?.createdAt || data?.updatedAt || 0).getTime();
  if (localResetAt > 0 && rowCreatedAt > 0 && rowCreatedAt < localResetAt - 5000) {
    return { ok: false, reason: 'pre_reset_record' }; // registro pré-reset, não vai pra cloud
  }
  // v7.68 — Payload montado ANTES dos guards, pra poder enfileirar em
  // qualquer cenário de indisponibilidade (disabled/não-inicializado/sem auth).
  const raw = { ...data, id: undefined, updatedAt: data.updatedAt || new Date().toISOString() };
  delete raw.id;
  if (currentUid) raw._writeUid = currentUid;
  if (cachedNickname) raw._writeDevice = cachedNickname;
  const payload = stripUndefined(raw);
  // v7.68 — Firebase desativado ou ainda não inicializado: ENFILEIRA em vez
  // de descartar silenciosamente. O flush entrega quando autenticar/ativar.
  if (!cfg.enabled || !currentDb) {
    pendingWrites.set(`${table}#${id}`, { table, id, payload, ts: Date.now() });
    persistPendingWrites();
    return { ok: false, queued: true, reason: !cfg.enabled ? 'disabled' : 'not_initialized' };
  }
  // Hotfix v7.6 — Bloqueia push de item tombstoned. Quando um device offline
  // volta a sincronizar, hooks Dexie podem disparar pushToRemote para itens
  // que o admin já tinha excluído. Sem essa guarda, o item RESSUSCITAVA no
  // Firebase e voltava pros demais devices.
  // v7.71 — comparação temporal: tombstone antigo NÃO mata registro novo que
  // reutilizou o id (auto-increment reinicia após Reset Total). Era a causa
  // do pedido SUMIR ao marcar "Pronto" no KDS (fluxo Cozinha→Entregador).
  try {
    const tombTimes = await getTombstoneTimes(table);
    const tombAt = tombTimes.get(Number(id));
    if (tombAt !== undefined) {
      const recCreatedAt = new Date(data?.createdAt || data?.updatedAt || 0).getTime();
      if (tombstoneKillsRecord(tombAt, recCreatedAt)) {
        // Apaga local também — se chegou aqui, é um hook tardio de antes do delete.
        try { await db.table(table).delete(id); } catch {}
        // Garante que o Firebase fica sem o registro mesmo se chegou outro push antes.
        try { await remove(ref(currentDb, `${cfg.deviceGroup}/${table}/${id}`)); } catch {}
        return { ok: false, reason: 'tombstoned' };
      }
      // Tombstone stale: limpa pra parar de reavaliar este id.
      try { await removeTombstone(table, Number(id)); } catch {}
      try { await remove(ref(currentDb, `${cfg.deviceGroup}/__tombstones__/${table}/${id}`)); } catch {}
    }
  } catch {}
  // Hotfix v6: aguarda auth resolver. Se o boot ainda está pendente, esse
  // await NÃO bloqueia indefinidamente (waitForAuth tem timeout interno).
  await waitForAuth();
  if (!isAuthenticated) {
    // Hotfix v5: enfileira em vez de descartar. Última versão vence (Map dedup).
    pendingWrites.set(`${table}#${id}`, { table, id, payload, ts: Date.now() });
    persistPendingWrites();
    return { ok: false, queued: true, reason: 'unauthenticated' };
  }
  try {
    // v8.06 — set() do SDK NUNCA rejeita com socket morto/hibernado: fica
    // pendente eternamente (era o "Processando..." infinito do KDS). Timeout
    // de 5s: estourou → cai no catch, reenfileira pra retransmissão
    // automática e chuta o socket pra reconectar.
    await Promise.race([
      set(ref(currentDb, `${cfg.deviceGroup}/${table}/${id}`), payload),
      new Promise((_, rej) => setTimeout(() => rej(new Error('push_timeout_5s')), 5000)),
    ]);
    if (lastSyncMs[table]) markSync(table, 'push');
    return { ok: true };
  } catch (err) {
    if (String(err?.message).includes('push_timeout')) {
      try { goOnline(currentDb); } catch {}
    }
    // eslint-disable-next-line no-console
    console.warn(`[FIREBASE SYNC] push ${table}#${id} falhou:`, err?.message || err);
    // v7.76 — Antes só PERMISSION_DENIED era reenfileirado; qualquer outra
    // falha (timeout, rede instável, desconexão momentânea do RTDB) era
    // descartada permanentemente sem nova tentativa, mesmo a UI dizendo
    // "será reenviada automaticamente" (ticket #207625 — pedidos "prontos"
    // que nunca chegavam ao entregador). Agora qualquer falha reenfileira —
    // pior caso é reenviar um payload que já chegou (idempotente, mesma chave).
    pendingWrites.set(`${table}#${id}`, { table, id, payload, ts: Date.now() });
    persistPendingWrites();
    return { ok: false, queued: true, reason: String(err?.message || err) };
  }
}

export async function removeFromRemote(table, id, stableId = null) {
  if (!currentDb || !SYNCED_TABLES.includes(table)) return;
  const cfg = await getFirebaseConfig();
  if (!cfg.enabled) return;
  // Hotfix v6: gate de auth antes de qualquer delete.
  await waitForAuth();
  if (!isAuthenticated) return;
  try {
    await remove(ref(currentDb, `${cfg.deviceGroup}/${table}/${id}`));
  } catch {}
  // Hotfix v7.6 — Replica tombstone no Firebase para outros devices receberem
  // a info de exclusão. Sem isso, device B que estava offline volta com item
  // local e RESSUSCITA o registro via hook updating.
  //
  // v7.46 — Para `menuItems`, usa `stableId` como chave no Firebase. UUIDs
  // são únicos globalmente; impede que tombstones de devices distintos
  // colidam no path quando ambos têm itens com local-id=5.
  // Tabelas legadas (commands/deliveryOrders/kdsQueue) continuam usando o
  // local-id como chave (sem colisão pois IDs são device-of-origin).
  try {
    const tombstoneKey = (STABLE_ID_TABLES.includes(table) && stableId) ? stableId : id;
    await set(ref(currentDb, `${cfg.deviceGroup}/__tombstones__/${table}/${tombstoneKey}`), {
      deletedAt: new Date().toISOString(),
      _writeUid: currentUid || null,
      _writeDevice: cachedNickname || null,
      // Inclui ambos os ids no payload pra que devices remotos consigam
      // localizar o registro local pelo stableId (preferido) ou localId (fallback).
      ...(stableId ? { stableId } : {}),
      ...(STABLE_ID_TABLES.includes(table) ? { localId: id } : {}),
    });
  } catch {}
}

// ════════════════════════════════════════════════════════════════════════
// Shared Config — chaves da whitelist sincronizam via __sharedConfig__/{key}
// ════════════════════════════════════════════════════════════════════════

async function pushSharedConfig(key, value, updatedAt) {
  if (!currentDb || !SHARED_CONFIG_KEYS.has(key)) return;
  const cfg = await getFirebaseConfig();
  if (!cfg.enabled) return;
  await waitForAuth();
  if (!isAuthenticated) return;
  try {
    const payload = stripUndefined({
      value,
      updatedAt: updatedAt || new Date().toISOString(),
      _writeUid: currentUid || undefined,
      _writeDevice: cachedNickname || undefined,
    });
    await set(ref(currentDb, `${cfg.deviceGroup}/__sharedConfig__/${key}`), payload);
    markSync('config', 'push');
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[FIREBASE SYNC] pushSharedConfig ${key} falhou:`, err?.message || err);
  }
}

async function removeSharedConfig(key) {
  if (!currentDb || !SHARED_CONFIG_KEYS.has(key)) return;
  const cfg = await getFirebaseConfig();
  if (!cfg.enabled) return;
  await waitForAuth();
  if (!isAuthenticated) return;
  try {
    await remove(ref(currentDb, `${cfg.deviceGroup}/__sharedConfig__/${key}`));
  } catch {}
}

/**
 * Listener live na branch __sharedConfig__. Aplica LWW por chave (updatedAt).
 * Marca chaves como "remote-applied" durante o write local pra evitar
 * que o hook updating reflua de volta pro Firebase.
 */
const inFlightConfigKeys = new Set();

/**
 * Hotfix v7.6 — Listener de tombstones replicados.
 * Path: `${deviceGroup}/__tombstones__/{table}/{id}` → { deletedAt, _writeUid, ... }
 * Quando outro device exclui um item, ele faz `set(...)` neste path.
 * Este listener importa o tombstone localmente (via addTombstone) E
 * apaga o registro do Dexie se ainda existir aqui. Listener é silencioso
 * (sem auto-loop): o addTombstone é dedup e o db.table().delete dispara
 * o hook deleting, mas o hook só re-pusha tombstone (idempotente).
 *
 * v7.11 — retorna Promise que resolve no PRIMEIRO snapshot, permitindo
 * o `init` aguardar a ingestão completa antes de subir o snapshot local
 * (eliminando a race entre listener e snapshot push).
 */
function attachTombstonesListener(deviceGroup) {
  if (tableListeners['__tombstones__']) return Promise.resolve();
  const r = ref(currentDb, `${deviceGroup}/__tombstones__`);
  return new Promise((resolveFirst) => {
    let first = true;
    const unsub = onValue(r, async (snap) => {
      // v7.73 — guard pelo estado real do DB (flag preso não silencia mais).
      if (!db.isOpen()) {
        if (first) { first = false; resolveFirst(); }
        return;
      }
      activeListenerWrites += 1;
      try {
        const val = snap.val();
        if (val && typeof val === 'object') {
          for (const [table, items] of Object.entries(val)) {
            if (!db.isOpen()) break;
            if (!SYNCED_TABLES.includes(table)) continue;
            if (!items || typeof items !== 'object') continue;
            const localNumericIds = await getTombstonedIds(table);
            const useStable = STABLE_ID_TABLES.includes(table);
            const localStableIds = useStable ? await getTombstonedStableIds(table) : null;

            for (const keyRaw of Object.keys(items)) {
              const payload = items[keyRaw] || {};
              // v7.46 — Chave do tombstone pode ser:
              //  - numérica (legacy: local-id Dexie) → para commands/deliveryOrders/kdsQueue
              //  - UUID string com hífens → para menuItems (stableId)
              const isNumericKey = /^\d+$/.test(keyRaw);
              const isStableKey = !isNumericKey && useStable;

              if (isStableKey) {
                // Tombstone por stableId (autoritativo cross-device).
                const stableId = keyRaw;
                // Adiciona localmente se ainda não tem.
                if (!localStableIds.has(stableId)) {
                  try { await addTombstone(table, { stableId, id: payload.localId, ...payload }); } catch {}
                }
                // Apaga qualquer registro local com esse stableId.
                try {
                  const allLocal = await db.table(table).toArray();
                  for (const row of allLocal) {
                    if (row.stableId === stableId) {
                      try { await db.table(table).delete(row.id); } catch {}
                    }
                  }
                } catch {}
              } else if (isNumericKey) {
                // Tombstone legado por local-id (back-compat).
                const id = Number(keyRaw);
                if (!Number.isFinite(id) || id <= 0) continue;
                if (!localNumericIds.has(id)) {
                  try {
                    const record = useStable && payload.stableId
                      ? { id, stableId: payload.stableId, ...payload }
                      : { id, ...payload };
                    await addTombstone(table, record);
                  } catch {}
                }
                try {
                  const existing = await db.table(table).get(id);
                  if (existing) {
                    // v7.71 — só apaga se o tombstone for MAIS NOVO que o
                    // registro (ids reutilizados pós-reset sobrevivem).
                    // deletedAt é o momento autoritativo da exclusão.
                    const recMs = new Date(existing.createdAt || existing.updatedAt || 0).getTime();
                    const tombSrc = payload.deletedAt || payload.at;
                    const tombMs = tombSrc ? new Date(tombSrc).getTime() : Infinity;
                    if (tombstoneKillsRecord(tombMs, recMs)) {
                      await db.table(table).delete(id);
                    }
                  }
                } catch {}
              }
            }
          }
        }
      } catch (err) {
        // v7.69 — .catch explícito: evita unhandled rejection quando o
        // Dexie fecha no meio do processamento de tombstones.
        // eslint-disable-next-line no-console
        console.warn('[FIREBASE SYNC] tombstones listener catch:', err?.message || err);
      } finally {
        activeListenerWrites -= 1;
        if (first) {
          first = false;
          resolveFirst();
        }
      }
    });
    tableListeners['__tombstones__'] = unsub;
    // Safety: se Firebase nunca retornar (rede ruim), libera após 5s
    setTimeout(() => { if (first) { first = false; resolveFirst(); } }, 5000);
  });
}

function attachSharedConfigListener(deviceGroup) {
  if (tableListeners['__sharedConfig__']) return;
  const r = ref(currentDb, `${deviceGroup}/__sharedConfig__`);
  const unsub = onValue(r, async (snap) => {
    // v7.73 — guard pelo estado real do DB (flag preso não silencia mais).
    if (!db.isOpen()) return;
    activeListenerWrites += 1;
    try {
      const val = snap.val();
      markSync('config', 'receive');
      if (!val || typeof val !== 'object') return;
      for (const [key, payload] of Object.entries(val)) {
        if (!db.isOpen()) break;
        if (!SHARED_CONFIG_KEYS.has(key)) continue;
        if (!payload || typeof payload !== 'object') continue;
        const remoteTs = new Date(payload.updatedAt || 0).getTime();
        const local = await db.config.get(key).catch(() => null);
        const localTs = new Date(local?.updatedAt || 0).getTime();
        if (!local || remoteTs > localTs + 1000) {
          try {
            inFlightConfigKeys.add(key);
            // Bypass hook updating — usamos Dexie.Table.update direto;
            // hook updating ainda dispara, mas o pushSharedConfig faz
            // dedup por updatedAt idêntico no Firebase (operação idempotente).
            await db.config.put({ key, value: payload.value, updatedAt: payload.updatedAt });
          } catch {} finally {
            inFlightConfigKeys.delete(key);
          }
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[FIREBASE SYNC] sharedConfig listener catch:', err?.message || err);
    } finally {
      activeListenerWrites -= 1;
    }
  }, (err) => {
    const msg = String(err?.message || err);
    if (/PERMISSION_DENIED|permission_denied/i.test(msg)) {
      // eslint-disable-next-line no-console
      console.warn('[FIREBASE SYNC] sharedConfig listener: PERMISSION_DENIED — segue offline.');
    } else {
      // eslint-disable-next-line no-console
      console.warn('[FIREBASE SYNC] sharedConfig listener erro:', err);
    }
  });
  tableListeners['__sharedConfig__'] = unsub;
}

const ORDER_STATUS_RANK = { awaiting_payment: 0, preparing: 1, pending: 1, ready: 2, delivering: 3, delivered: 4 };

// v7.83 (ticket 218940) -- Acima disto, uma claim sem pedido correspondente e
// tratada como orfa e a ponte assume. Generoso de proposito: o checkout leva
// ~1-2s entre reivindicar e gravar, e um takeover cedo demais recria a
// duplicidade que estamos justamente eliminando.
const STALE_CLAIM_TAKEOVER_MS = 90000;

/**
 * v7.83 (ticket 218940) — Reivindica a CRIACAO do pedido de um pagamento.
 *
 * O mesmo pagamento tem dois criadores possiveis de ticket de cozinha: o
 * navegador do cliente (`createOrderOnce` em pages/DeliveryPage.jsx) e a ponte
 * do backend (`attachBackendOrdersListener`, abaixo). As checagens da ponte por
 * mpPaymentId dependem de TIMING: se o device do cliente ainda nao espelhou a
 * linha dele pro Firebase, a ponte nao acha nada, conclui que o pedido nao
 * existe e cria uma SEGUNDA linha. Foi exatamente o que gerou os tickets #34 e
 * #35 do mesmo pagamento -- um device, um pagamento, 51s de intervalo.
 *
 * Esta transacao e o unico arbitro atomico entre os dois caminhos: quem gravar
 * `backendOrderClaims/{mpPaymentId}` primeiro fica responsavel pelo pedido; o
 * outro lado aborta e recebe a linha pelo sync normal de tabela.
 *
 * @returns {Promise<'won'|'lost'|'unavailable'>} 'unavailable' quando nao ha
 *   Firebase utilizavel (offline, sem config, transacao falhou). Nesse caso o
 *   chamador DEVE seguir e criar o pedido: um ticket duplicado incomoda a
 *   cozinha, um pedido pago que nunca aparece perde a venda e o cliente.
 */
export async function claimPaymentForOrderCreation(mpPaymentId) {
  const id = String(mpPaymentId || '').trim();
  if (!id || !currentDb) return 'unavailable';
  let group;
  try {
    group = await getDeviceGroup();
  } catch {
    return 'unavailable';
  }
  if (!group) return 'unavailable';
  try {
    const claimRef = ref(currentDb, `${group}/backendOrderClaims/${id}`);
    const txResult = await runTransaction(claimRef, (current) => {
      if (current) return; // aborta -- ja reivindicado por outro caminho
      // v7.83 -- Grava QUEM e QUANDO (antes era `true`). A ponte usa a idade
      // da claim para decidir se o dono morreu antes de criar o pedido.
      return { by: 'checkout', at: Date.now() };
    });
    return txResult.committed ? 'won' : 'lost';
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[FIREBASE SYNC] claim de criacao falhou p/ ${id}:`, err?.message || err);
    return 'unavailable';
  }
}

/**
 * v7.83 (ticket 218940) — Procura no Firebase o pedido de um pagamento.
 *
 * Usado pelo checkout quando ele PERDE a claim: o dono (normalmente a ponte do
 * backend) ja gravou a linha, mas o sync local pode ainda nao ter trazido. As
 * linhas de `deliveryOrders` sao chaveadas pelo id do Dexie, entao a chave do
 * no E o id que a UI precisa para abrir a tela de confirmacao.
 *
 * @returns {Promise<number|null>} id do pedido, ou null se nao existir
 */
export async function findRemoteOrderIdByPayment(mpPaymentId) {
  const id = String(mpPaymentId || '').trim();
  if (!id || !currentDb) return null;
  let group;
  try {
    group = await getDeviceGroup();
  } catch {
    return null;
  }
  if (!group) return null;
  try {
    // Sem `.indexOn: "mpPaymentId"` nas regras do RTDB (v7.72), a query
    // indexada devolve 400 -- le o no inteiro e filtra no cliente. Volume de
    // um restaurante, entao e barato.
    const snap = await get(ref(currentDb, `${group}/deliveryOrders`));
    const val = snap.val();
    if (!val || typeof val !== 'object') return null;
    for (const [key, row] of Object.entries(val)) {
      if (row && String(row.mpPaymentId) === id) {
        const parsed = Number(key);
        return Number.isFinite(parsed) ? parsed : null;
      }
    }
    return null;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[FIREBASE SYNC] busca remota de pedido falhou p/ ${id}:`, err?.message || err);
    return null;
  }
}

/**
 * v7.71 — Ponte MongoDB -> Firebase RTDB (lado cliente) — ticket #207625.
 *
 * Escuta `{deviceGroup}/backendOrders/{mpPaymentId}` — path escrito pelo
 * backend (mp_webhooks.py) quando promove um pedido apos confirmacao do
 * Mercado Pago. Diferente de attachTableListener, o path aqui e chaveado
 * por `mpPaymentId` (string do MP), nao pelo ID local autoIncrement do
 * Dexie — o backend nao tem como gerar/prever esse ID.
 *
 * Faz upsert em `deliveryOrders`/`kdsQueue` por `mpPaymentId`:
 *  - Se ja existe localmente (navegador do cliente seguiu aberto e criou o
 *    pedido pelo caminho normal em createOrderOnce), so atualiza o status
 *    se o backend estiver "a frente" (evita regressao de status).
 *  - Se nao existe (navegador fechou antes da confirmacao — a causa raiz
 *    do ticket), cria os registros do zero, espelhando createOrderOnce().
 */
function attachBackendOrdersListener(deviceGroup) {
  if (tableListeners['__backendOrders__']) return;
  const r = ref(currentDb, `${deviceGroup}/backendOrders`);
  const unsub = onValue(r, async (snap) => {
    // v7.73 — guard pelo estado real do DB (flag preso não silencia mais).
    if (!db.isOpen()) return;
    activeListenerWrites += 1;
    let bridgeApplied = 0;
    try {
      const val = snap.val();
      if (!val || typeof val !== 'object') return;
      for (const [paymentId, payload] of Object.entries(val)) {
        if (!db.isOpen()) break;
        // v7.82 — currentDb pode ficar null durante re-init (teardown em
        // curso). Sem este guard, get/runTransaction estouram
        // '_checkNotDeleted' e o pedido da ponte fica órfão até o resync.
        if (!currentDb) break;
        if (!payload || typeof payload !== 'object') continue;
        const mpPaymentId = payload.mpPaymentId || paymentId;
        const backendStatus = payload.status || 'preparing';
        const orderSnapshot = payload.order || {};
        // TICKET 207625 - INSTRUMENTACAO (somente log): marca quando a ponte do
        // backend VE um pedido pago, pra medir a latencia real ate a UI da cozinha.
        try { console.log('[BRIDGE 207625] backendOrder visto mpPaymentId=%s status=%s at=%s', mpPaymentId, backendStatus, new Date().toISOString()); } catch {}
        try {
          const existing = await db.deliveryOrders.where('mpPaymentId').equals(mpPaymentId).first();
          if (existing) {
            const existingRank = ORDER_STATUS_RANK[existing.status] ?? 0;
            const backendRank = ORDER_STATUS_RANK[backendStatus] ?? 0;
            if (backendRank > existingRank) {
              await db.deliveryOrders.update(existing.id, { status: backendStatus, updatedAt: new Date().toISOString() });
              bridgeApplied += 1;
            }
            continue;
          }

          // v7.82 — Blindagem anti-reintrodução: ponte de pedido já
          // FINALIZADO/CANCELADO nunca recria registro em tela ativa.
          if (backendStatus === 'delivered' || backendStatus === 'cancelled') {
            continue;
          }

          // v7.72 -- Dedup camada 1 (cross-device): checa o Firebase
          // deliveryOrders inteiro (nao so o Dexie local) -- outro device
          // (ex: o proprio celular do cliente) pode ja ter criado este
          // pedido pelo fluxo normal (createOrderOnce) e feito o mirror pro
          // Firebase antes deste listener rodar aqui. Sem essa checagem,
          // cada device sem o registro local cria sua PROPRIA copia --
          // exatamente o bug de pedidos duplicados achado no ticket #207625
          // (3 entradas deliveryOrders para o mesmo mpPaymentId).
          try {
            // v7.72 -- Sem `.indexOn: "mpPaymentId"` configurado nas regras
            // do RTDB (confirmado por teste direto: query indexada retorna
            // 400 "Index not defined"), busca a colecao inteira e filtra no
            // cliente. deliveryOrders e pequeno (volume de um restaurante),
            // entao isso e barato -- nao depende de mudanca de regras que
            // nao temos acesso admin para fazer.
            const allSnap = await get(ref(currentDb, `${deviceGroup}/deliveryOrders`));
            const allVal = allSnap.val();
            const alreadyExists = allVal && typeof allVal === 'object' &&
              Object.values(allVal).some((o) => o && o.mpPaymentId === mpPaymentId);
            if (alreadyExists) {
              continue;
            }
          } catch (qErr) {
            // eslint-disable-next-line no-console
            console.warn(`[FIREBASE SYNC] backendOrders dedup check falhou p/ ${mpPaymentId} (seguindo com claim):`, qErr?.message || qErr);
          }

          // v7.72 -- Dedup camada 2 (mesma corrida entre bridges): varios
          // devices podem ter este listener ativo ao mesmo tempo (cozinha +
          // entregador + qualquer aba aberta). Claim atomico via transacao
          // -- so quem comitar `claimed: true` primeiro segue adiante; os
          // demais abortam a criacao (o registro deles vai chegar via o
          // attachTableListener normal quando o vencedor terminar).
          // v7.73 -- Path SEPARADO de backendOrders (nao um filho dele). O claim
          // antigo escrevia em backendOrders/{id}/claimed, que e um filho do
          // proprio no que este listener observa -- cada claim re-disparava
          // o onValue de TODOS os devices conectados, causando reprocessamento
          // em cascata e lentidao severa (ticket #207625, regressao pos-deploy).
          const claimRef = ref(currentDb, `${deviceGroup}/backendOrderClaims/${paymentId}`);
          let txResult;
          try {
            txResult = await runTransaction(claimRef, (current) => {
              if (current) return; // aborta -- ja reivindicado por outro device
              return { by: 'bridge', at: Date.now() };
            });
          } catch (txErr) {
            // eslint-disable-next-line no-console
            console.warn(`[FIREBASE SYNC] backendOrders claim falhou p/ ${mpPaymentId}:`, txErr?.message || txErr);
            continue; // sem claim confiavel, nao arrisca duplicar
          }
          if (!txResult.committed) {
            // v7.83 (ticket 218940) -- Perdemos a claim: o checkout do cliente
            // (ou outro device) assumiu este pagamento. As duas camadas de
            // dedup acima JA provaram que nao existe pedido em lugar nenhum --
            // logo, se a claim estiver velha, o dono provavelmente morreu antes
            // de gravar (aba fechada durante o Pix). Nesse caso assumimos: um
            // pedido pago que nunca chega na cozinha e o pior desfecho possivel.
            const held = txResult.snapshot?.val();
            const heldAt = held && typeof held === 'object' ? Number(held.at) || 0 : 0;
            const orphaned = !heldAt || (Date.now() - heldAt) > STALE_CLAIM_TAKEOVER_MS;
            if (!orphaned) {
              continue; // dono ativo, esta gravando agora -- nao duplica
            }
            // eslint-disable-next-line no-console
            console.warn(`[FIREBASE SYNC] claim de ${mpPaymentId} orfa (sem pedido) -- ponte assumindo`);
          }

          // Nao existe em lugar nenhum e a claim foi vencida por este device.
          // Cria do zero, espelhando createOrderOnce().
          const newOrder = {
            customerName: orderSnapshot.customerName || 'Cliente',
            customerPhone: orderSnapshot.customerPhone || '',
            neighborhood: orderSnapshot.neighborhood || '',
            address: orderSnapshot.address || '',
            landmark: orderSnapshot.landmark || '',
            customerLat: orderSnapshot.customerLat,
            customerLng: orderSnapshot.customerLng,
            items: orderSnapshot.items || [],
            subtotal: orderSnapshot.subtotal,
            deliveryFee: orderSnapshot.deliveryFee,
            total: orderSnapshot.total,
            deliveryDistance: orderSnapshot.deliveryDistance,
            status: 'preparing',
            paymentMethod: orderSnapshot.paymentMethod || 'Pix',
            paidAt: payload.promotedAt || new Date().toISOString(),
            mpPaymentId,
            createdAt: new Date().toISOString(),
            bridgedFromBackend: true,
          };
          const id = await db.deliveryOrders.add(newOrder);
          await db.kdsQueue.add({
            orderId: id,
            source: 'delivery',
            status: 'pending',
            customerName: newOrder.customerName,
            items: newOrder.items,
            mpPaymentId,
            createdAt: new Date().toISOString(),
          });
          bridgeApplied += 1;
          // eslint-disable-next-line no-console
          console.log(`[FIREBASE SYNC] pedido recuperado via ponte backend: mpPaymentId=${mpPaymentId} (navegador de origem havia fechado)`);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn(`[FIREBASE SYNC] backendOrders upsert falhou p/ ${mpPaymentId}:`, e?.message || e);
        }
      }
      // v7.81 — Tempo real na UI: pedidos que chegam pela PONTE do backend
      // (webhook/reconciliador MP) também notificam KDS/Entregador na hora —
      // antes só o listener de tabela normal disparava este evento, e o
      // pedido bridged só aparecia no próximo tick do polling.
      // v7.81 (fix) -- este listener grava em db.deliveryOrders nos dois
      // ramos (update do existente OU criacao do zero); so o ramo de criacao
      // tambem toca kdsQueue. 'deliveryOrders' e o table correto pro filtro
      // exato do EntregadorPage (=== 'deliveryOrders'); KDSPage aceita ambos
      // ('kdsQueue' || 'deliveryOrders') entao continua funcionando.
      if (bridgeApplied > 0) {
        try { console.log('[BRIDGE 207625] aplicou %s mudanca(s) de backendOrders no Dexie at=%s', bridgeApplied, new Date().toISOString()); } catch {}
        try {
          const _u = (process.env.REACT_APP_BACKEND_URL || '') + '/api/diag/checkout';
          const _b = new Blob([JSON.stringify({ event: 'bridge_applied', count: bridgeApplied, ts: new Date().toISOString() })], { type: 'application/json' });
          if (typeof navigator !== 'undefined' && navigator.sendBeacon) navigator.sendBeacon(_u, _b);
        } catch {}
        try { window.dispatchEvent(new CustomEvent('kaven:remote-merge', { detail: { table: 'deliveryOrders', count: bridgeApplied, bridged: true } })); } catch {}
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[FIREBASE SYNC] backendOrders listener catch:', err?.message || err);
    } finally {
      activeListenerWrites -= 1;
    }
  }, (err) => {
    const msg = String(err?.message || err);
    if (/PERMISSION_DENIED|permission_denied/i.test(msg)) {
      // eslint-disable-next-line no-console
      console.warn('[FIREBASE SYNC] backendOrders listener: PERMISSION_DENIED — segue offline.');
    } else {
      // eslint-disable-next-line no-console
      console.warn('[FIREBASE SYNC] backendOrders listener erro:', err);
    }
  });
  tableListeners['__backendOrders__'] = unsub;
}

/**
 * Snapshot inicial: sobe todas as chaves locais da whitelist para o RTDB.
 * Multi-path bulk (1 round-trip).
 */
async function pushSharedConfigSnapshot(deviceGroup) {
  await waitForAuth();
  if (!isAuthenticated) return;
  try {
    const all = await db.config.toArray();
    const updates = {};
    for (const row of all) {
      if (!SHARED_CONFIG_KEYS.has(row.key)) continue;
      updates[`${deviceGroup}/__sharedConfig__/${row.key}`] = stripUndefined({
        value: row.value,
        updatedAt: row.updatedAt || new Date().toISOString(),
      });
    }
    if (Object.keys(updates).length === 0) return;
    await update(ref(currentDb), updates);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[FIREBASE SYNC] pushSharedConfigSnapshot falhou:', err?.message || err);
  }
}

/**
 * Primeira sincronização: envia registros locais existentes para o RTDB.
 */
async function pushLocalSnapshot(deviceGroup) {
  // Hotfix v6: re-checa o marker de reset antes de subir snapshot.
  // Se outro device fez reset DEPOIS que iniciamos (race condition durante
  // os 1.5s de delay do setTimeout), abortamos pra não republicar lixo.
  try {
    const snap = await get(ref(currentDb, `${deviceGroup}/__resetAt__`));
    const remoteResetAt = snap.exists() ? Number(snap.val()) : 0;
    const localResetAt = Number(localStorage.getItem('kaven_resetAt_local') || 0);
    if (remoteResetAt > localResetAt) {
      // eslint-disable-next-line no-console
      console.warn('[FIREBASE SYNC] Snapshot abortado — reset mais novo detectado durante delay.');
      const { resetDatabase } = await import('@/db');
      await resetDatabase();
      localStorage.setItem('kaven_resetAt_local', String(remoteResetAt));
      pendingWrites.clear();
      return;
    }
  } catch {}
  // Hotfix v6.3 — todas as tabelas em paralelo, e dentro de cada tabela,
  // multi-path UPDATE atômico (1 round-trip vs. N round-trips sequenciais).
  // Pra 100 comandas, isso reduz de ~5s pra ~150ms.
  const localResetAt = Number(localStorage.getItem('kaven_resetAt_local') || 0);

  // v7.11 — Busca o estado atual de __tombstones__ no Firebase ANTES de
  // qualquer push. Mescla com tombstones locais por tabela para garantir
  // que itens excluídos em outros devices NÃO sejam ressuscitados pelo
  // snapshot deste device (caso ele estivesse offline quando a exclusão ocorreu).
  //
  // v7.46 — Suporta DOIS tipos de chave no path __tombstones__:
  //  - numérica: local-id (legacy, usado por commands/deliveryOrders/kdsQueue)
  //  - UUID string: stableId (novo, usado por menuItems)
  let remoteTombstonesByTable = {};        // table → Map<localId, atMs>
  let remoteStableTombstonesByTable = {};  // table → Set<stableId string>
  try {
    const tsSnap = await get(ref(currentDb, `${deviceGroup}/__tombstones__`));
    const tsVal = tsSnap.exists() ? tsSnap.val() : null;
    if (tsVal && typeof tsVal === 'object') {
      for (const [t, items] of Object.entries(tsVal)) {
        if (!items || typeof items !== 'object') continue;
        const numericTimes = new Map(); // v7.71 — id → at(ms) pra comparação temporal
        const stableIds = new Set();
        for (const k of Object.keys(items)) {
          if (/^\d+$/.test(k)) {
            const n = Number(k);
            if (Number.isFinite(n) && n > 0) {
              const src = items[k]?.deletedAt || items[k]?.at;
              const ms = src ? new Date(src).getTime() : Infinity;
              numericTimes.set(n, Number.isFinite(ms) ? ms : Infinity);
            }
          } else {
            // UUID/string key → é um stableId
            stableIds.add(k);
          }
          // Também coleta stableId do payload (se presente no formato novo)
          const sid = items[k]?.stableId;
          if (typeof sid === 'string' && sid.length > 0) stableIds.add(sid);
        }
        remoteTombstonesByTable[t] = numericTimes;
        remoteStableTombstonesByTable[t] = stableIds;
      }
    }
  } catch (err) {
    // v7.83 — Sem visão dos tombstones remotos, subir snapshot é ROLETA
    // RUSSA: era um dos vetores de ressurreição (device offline durante a
    // exclusão subia os registros apagados). Aborta — o push acontece na
    // próxima oportunidade com tombstones legíveis.
    // eslint-disable-next-line no-console
    console.warn('[FIREBASE SYNC] snapshot ABORTADO — falha ao buscar __tombstones__ remotos:', err?.message || err);
    return;
  }

  await Promise.all(SYNCED_TABLES.map(async (table) => {
    try {
      if (!db.tables || !db.tables.some((t) => t.name === table)) return;
      const localTombTimes = await getTombstoneTimes(table);
      const remoteTombTimes = remoteTombstonesByTable[table] || new Map();
      // v7.71 — União local ∪ remoto por id, mantendo o `at` mais recente,
      // pra comparação temporal (ids reutilizados pós-reset sobrevivem).
      const tombstoneTimes = new Map(localTombTimes);
      for (const [id, atMs] of remoteTombTimes) {
        const cur = tombstoneTimes.get(id);
        if (cur === undefined || atMs > cur) tombstoneTimes.set(id, atMs);
      }
      // v7.46 — Adicionalmente, para tabelas com stableId, computa o set
      // de stableIds tombstoned (local ∪ remoto). Itens cujo stableId
      // está nesse set NÃO são republicados, mesmo que o local-id deles
      // não esteja tombstoned neste device.
      const useStable = STABLE_ID_TABLES.includes(table);
      const stableTombstones = useStable
        ? new Set([
            ...(await getTombstonedStableIds(table)),
            ...(remoteStableTombstonesByTable[table] || new Set()),
          ])
        : null;

      // v7.74 — Busca o estado REMOTO atual da tabela ANTES de publicar.
      // Sem isso, pushLocalSnapshot sobrescrevia cegamente o Firebase com
      // o snapshot local inteiro toda vez que o device reiniciava o sync
      // (todo reload chama initFirebaseSync -> pushLocalSnapshot), mesmo
      // que outro device tivesse avançado o status depois -- um pedido
      // marcado "ready"/"delivered" em outro device reaparecia como
      // "pending"/"preparing" quando ESTE device reconectava com dados
      // antigos em cache local. Causa raiz dos "fantasmas" do ticket #207625
      // que sobreviveram mesmo depois da correcao do loop de feedback.
      let remoteRows = {};
      try {
        const remoteSnap = await get(ref(currentDb, `${deviceGroup}/${table}`));
        remoteRows = remoteSnap.val() || {};
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(`[FIREBASE SYNC] snapshot: falha ao buscar estado remoto de ${table} (seguindo sem LWW):`, e?.message || e);
      }

      const rows = await db.table(table).toArray().catch(() => []);
      const updates = {};
      for (const row of rows) {
        const { id, ...rest } = row;
        if (!Number.isFinite(id) || id <= 0) continue;
        // Chequa primeiro por stableId (autoritativo). Se está tombstoned
        // por stableId, apaga local e nunca republica.
        if (useStable && row.stableId && stableTombstones.has(row.stableId)) {
          try { await db.table(table).delete(id); } catch {}
          continue;
        }
        const rowCreatedAt = new Date(rest.createdAt || rest.updatedAt || 0).getTime();
        const tombAt = tombstoneTimes.get(id);
        if (tombAt !== undefined && tombstoneKillsRecord(tombAt, rowCreatedAt)) {
          // v7.11 — Se está tombstoned remotamente mas ainda existe local,
          // apaga local também para fechar o ciclo (defesa contra ressurreição).
          try { await db.table(table).delete(id); } catch {}
          continue;
        }
        if (localResetAt > 0 && rowCreatedAt > 0 && rowCreatedAt < localResetAt - 5000) {
          try { await db.table(table).delete(id); } catch {}
          continue;
        }
        // v7.74 — Last-write-wins: só publica se local for mais novo (ou
        // se o remoto simplesmente não existir ainda). Espelha a mesma
        // comparação que attachTableListener já fazia do lado de recepção,
        // agora também do lado de envio.
        const remote = remoteRows[String(id)];
        if (remote && typeof remote === 'object') {
          const remoteTs = new Date(remote.updatedAt || remote.createdAt || 0).getTime();
          const localTs = new Date(rest.updatedAt || rest.createdAt || 0).getTime();
          if (remoteTs > localTs) continue; // remoto mais novo -- não sobrescreve
        }
        updates[`${deviceGroup}/${table}/${id}`] = stripUndefined({
          ...rest,
          updatedAt: rest.updatedAt || rest.createdAt || new Date().toISOString(),
        });
      }
      if (Object.keys(updates).length === 0) return;
      // Multi-path update: 1 chamada RTDB para todas as linhas dessa tabela.
      await update(ref(currentDb), updates).catch((e) => {
        // eslint-disable-next-line no-console
        console.warn(`[FIREBASE SYNC] snapshot bulk ${table} fail:`, e?.message || e);
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[FIREBASE SYNC] snapshot ${table} falhou`, err?.message || err);
    }
  }));
}

/**
 * Hook Dexie: sempre que houver create/update/delete nas 3 tabelas,
 * espelha automaticamente no Firebase (se conectado).
 * Chamado uma única vez no bootstrap.
 *
 * Fase 27 — guard de idempotência via flag global. Se chamado 2x (StrictMode
 * dev ou re-render acidental), evita duplicar hooks que causariam tempestade
 * de pushes/audit logs.
 *
 * IMPORTANTE: os callbacks dos hooks Dexie rodam DENTRO da transaction
 * original. Se chamarmos `pushToRemote` direto, ele tentará abrir `db.config`
 * dentro de uma transaction limitada a `commands`, o que quebra com
 * "object store not found". Por isso usamos `setTimeout(0)` para deferir.
 */
let dexieHooksAttached = false;
export function attachDexieHooks() {
  if (dexieHooksAttached) {
    // eslint-disable-next-line no-console
    console.log('[FIREBASE SYNC] Dexie hooks já anexados — ignorando reanexar');
    return;
  }
  dexieHooksAttached = true;
  for (const table of SYNCED_TABLES) {
    const t = db.table(table);
    t.hook('creating', function (primKey, obj) {
      // Hotfix v5: garante updatedAt fresco no momento exato do write.
      // Sem isso, o push usa o updatedAt antigo do payload e o listener no
      // outro device descarta via LWW (`remoteTs > localTs + 1000` = false).
      const now = new Date().toISOString();
      if (!obj.updatedAt) obj.updatedAt = now;
      if (!obj.createdAt) obj.createdAt = now;
      // v7.78 -- Este write e so a aplicacao local de um valor que ja veio
      // do Firebase (attachTableListener via LWW) -- nao reenvia. Ver
      // applyingRemoteSync no topo do arquivo.
      if (applyingRemoteSyncDepth > 0) return;
      // v7.85 (#207625) -- carimba stableId (UUID) em CRIACOES LOCAIS de
      // tabelas com chave estavel. Registros vindos do remoto (applyingRemoteSync)
      // ja retornaram acima e mantem o stableId do criador -> convergencia
      // entre aparelhos. Ponto unico: cobre pedido/kds/comanda/menu de uma vez.
      if (!obj.stableId && STABLE_ID_TABLES.includes(table)) {
        obj.stableId = (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : `st-${new Date(now).getTime()}-${primKey || ''}-${(obj.orderId ?? obj.number ?? '')}`;
      }
      this.onsuccess = (id) => {
        const snapshot = { ...obj, id };
        setTimeout(() => {
          pushToRemote(table, id, snapshot);
          logAudit({
            table, action: AUDIT_ACTIONS.create, record: snapshot,
            deviceUid: currentUid, deviceNickname: cachedNickname,
          });
        }, 0);
      };
    });
    t.hook('updating', function (modifications, primKey, obj) {
      // v7.78 -- Este write e so a aplicacao local de um valor que ja veio
      // do Firebase (attachTableListener via LWW): preserva o updatedAt que
      // ja veio no payload remoto e nao reenvia. Sem isso, qualquer device
      // que so estivesse re-sincronizando historico carimbava um now() novo
      // incondicionalmente e reenviava pro Firebase, o que fazia outros
      // devices reaplicarem e re-carimbarem de novo -- efeito cascata que
      // fazia pedidos ja concluidos ha semanas parecerem "atualizados agora"
      // em loop entre os aparelhos (ticket #207625, causa raiz dos
      // "pedidos fantasmas"). Ver applyingRemoteSync no topo do arquivo.
      if (applyingRemoteSyncDepth > 0) return;
      // Hotfix v5: SEMPRE refresca updatedAt em qualquer modificação.
      // Mutamos o objeto `modifications` (Dexie permite isso dentro do hook)
      // para que o registro local fique consistente com o que vai pro RTDB.
      const now = new Date().toISOString();
      // eslint-disable-next-line no-param-reassign
      modifications.updatedAt = now;
      this.onsuccess = () => {
        const next = { ...obj, ...modifications, id: primKey, updatedAt: now };
        setTimeout(() => {
          pushToRemote(table, primKey, next);
          // Só loga updates "significativos" (status/total/paymentMethod) pra não poluir
          const sigKeys = ['status', 'paymentMethod', 'total', 'finalTotal', 'assignedTo'];
          if (Object.keys(modifications || {}).some((k) => sigKeys.includes(k))) {
            logAudit({
              table, action: AUDIT_ACTIONS.update, record: next, modifications,
              deviceUid: currentUid, deviceNickname: cachedNickname,
            });
          }
        }, 0);
      };
    });
    t.hook('deleting', function (primKey, obj) {
      this.onsuccess = () => {
        setTimeout(async () => {
          // Hotfix v7.6 — Tombstone universal antes do remove no Firebase.
          // Sem isso, um device offline que volta com o item ainda local
          // dispara `pushToRemote` e RESSUSCITA o registro. Isso era visível
          // como "itens excluídos no Cardápio voltam sozinhos".
          // v7.46 — Para `menuItems`, INCLUI `stableId` (UUID consistente
          // entre devices). O tombstone agora é dedup-zado por stableId,
          // não por local-id Dexie. Isso impede que deletar item id=5 num
          // device apague o item id=5 (logicamente diferente) em outro.
          try {
            const tombstoneRecord = obj?.stableId
              ? { id: primKey, stableId: obj.stableId, name: obj?.name }
              : { id: primKey, name: obj?.name };
            await addTombstone(table, tombstoneRecord);
          } catch {}
          removeFromRemote(table, primKey, obj?.stableId || null);
          logAudit({
            table, action: AUDIT_ACTIONS.delete, record: { ...obj, id: primKey },
            deviceUid: currentUid, deviceNickname: cachedNickname,
          });
        }, 0);
      };
    });
  }

  // ────────────────────────────────────────────────────────────────────────
  // Hooks especiais para `db.config` (key-value compartilhada).
  // Só sincroniza chaves da WHITELIST `SHARED_CONFIG_KEYS`.
  // Path: ${deviceGroup}/__sharedConfig__/{key} = { value, updatedAt }.
  // ────────────────────────────────────────────────────────────────────────
  const cfgTable = db.config;
  cfgTable.hook('creating', function (primKey, obj) {
    const key = obj?.key;
    if (!key || !SHARED_CONFIG_KEYS.has(key)) return;
    obj.updatedAt = new Date().toISOString();
    this.onsuccess = () => {
      setTimeout(() => pushSharedConfig(key, obj.value, obj.updatedAt), 0);
    };
  });
  cfgTable.hook('updating', function (modifications, primKey, obj) {
    const key = (modifications && modifications.key) || obj?.key;
    if (!key || !SHARED_CONFIG_KEYS.has(key)) return;
    const now = new Date().toISOString();
    // eslint-disable-next-line no-param-reassign
    modifications.updatedAt = now;
    this.onsuccess = () => {
      const newValue = 'value' in (modifications || {}) ? modifications.value : obj.value;
      setTimeout(() => pushSharedConfig(key, newValue, now), 0);
    };
  });
  cfgTable.hook('deleting', function (primKey) {
    if (!SHARED_CONFIG_KEYS.has(primKey)) return;
    this.onsuccess = () => {
      setTimeout(() => removeSharedConfig(primKey), 0);
    };
  });
}

/**
 * Teste rápido de conectividade: retorna promise que resolve
 * true se conectou em X ms, false caso contrário.
 */
export async function pingFirebase(timeoutMs = 4000) {
  if (!currentDb) return false;
  return new Promise((resolve) => {
    const connRef = ref(currentDb, '.info/connected');
    let settled = false;
    const finish = (result) => {
      if (settled) return;
      settled = true;
      // off() é seguro: desliga TODOS os listeners do connRef criados acima,
      // sem depender da variável `cb` (que pode não estar inicializada ainda
      // se o snapshot chegar síncrono — bug do TDZ em iOS Safari).
      off(connRef);
      clearTimeout(to);
      resolve(result);
    };
    const to = setTimeout(() => finish(false), timeoutMs);
    onValue(connRef, (snap) => {
      if (snap.val() === true) finish(true);
    });
  });
}

// Helpers para UI
export const SyncAPI = {
  init: initFirebaseSync,
  teardown: teardownFirebaseSync,
  state: getSyncState,
  subscribe: subscribeSyncState,
  ping: pingFirebase,
  goOnline: () => currentDb && goOnline(currentDb),
  goOffline: () => currentDb && goOffline(currentDb),
  retryAuth: async () => {
    if (!currentAuth) return { ok: false, reason: 'not_initialized' };
    try {
      const cred = await signInAnonymously(currentAuth);
      isAuthenticated = true;
      currentUid = cred?.user?.uid || null;
      lastAuthError = null;
      emit();
      // Drena writes que falharam por PERMISSION_DENIED antes
      flushPendingWrites();
      return { ok: true, uid: currentUid };
    } catch (err) {
      lastAuthError = { code: err?.code || '', message: err?.message || String(err) };
      emit();
      return { ok: false, error: lastAuthError };
    }
  },
  /**
   * Apaga TODO o conteúdo das tabelas sincronizadas no Firebase RTDB
   * para o `deviceGroup` ativo. Usado pelo botão "RESET TOTAL" do Admin
   * pra eliminar lixo de testes antigos que continua re-sincronizando.
   *
   * Hotfix v6: Marca `__resetAt__` no Firebase ANTES de apagar.
   * Outros devices, ao se conectarem, vão detectar esse marker
   * (`syncResetMarker`) e auto-limpar seus dados locais ANTES de subir
   * snapshot — impede que comandas-fantasma voltem por re-push.
   *
   * Também pausa os hooks Dexie durante a operação local para evitar
   * que `resetDatabase` dispare pushes do reset propriamente dito.
   */
  wipeRemote: async () => {
    if (!currentDb) return { ok: false, reason: 'not_connected' };
    // Hotfix v6: aguarda auth antes de tentar wipe.
    await waitForAuth();
    if (!isAuthenticated) return { ok: false, reason: 'not_authenticated' };
    const cfg = await getFirebaseConfig();
    if (!cfg.enabled) return { ok: false, reason: 'disabled' };
    const now = Date.now();
    const deleted = {};
    // 1. Marca __resetAt__ ANTES de apagar dados — garante que mesmo se a
    //    operação falhar no meio, outros devices ainda detectem o reset.
    try {
      await set(ref(currentDb, `${cfg.deviceGroup}/__resetAt__`), now);
      // Persiste localmente também — este device é a fonte do reset.
      localStorage.setItem('kaven_resetAt_local', String(now));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[FIREBASE] __resetAt__ falhou:', err?.message || err);
    }
    // 2. Apaga cada tabela sincronizada.
    for (const table of SYNCED_TABLES) {
      try {
        await set(ref(currentDb, `${cfg.deviceGroup}/${table}`), null);
        deleted[table] = true;
      } catch (err) {
        deleted[table] = false;
        // eslint-disable-next-line no-console
        console.warn(`[FIREBASE] wipe ${table} falhou:`, err?.message || err);
      }
    }
    // 3. Limpa caches em memória pra não republicar nada antigo
    pendingWrites.clear();
    mergedVersions.clear();
    resolvedConflicts.clear();
    return { ok: true, deleted, resetAt: now };
  },

  /**
   * v7.50 — Lê TODO o conteúdo de uma tabela no Firebase RTDB (no
   * deviceGroup ativo). Usado pelo painel de Limpeza do Cardápio Fantasma
   * para listar exatamente o que está armazenado remotamente.
   *
   * Retorna `{ ok: true, items: [{ fbKey, ...row }] }` ou
   * `{ ok: false, reason }`.
   */
  readRemoteTable: async (table) => {
    if (!currentDb) return { ok: false, reason: 'not_connected' };
    await waitForAuth();
    if (!isAuthenticated) return { ok: false, reason: 'not_authenticated' };
    const cfg = await getFirebaseConfig();
    if (!cfg.enabled) return { ok: false, reason: 'disabled' };
    try {
      const r = ref(currentDb, `${cfg.deviceGroup}/${table}`);
      const snap = await get(r);
      const val = snap?.val();
      if (!val || typeof val !== 'object') {
        return { ok: true, items: [], deviceGroup: cfg.deviceGroup };
      }
      const items = Object.entries(val)
        .filter(([, v]) => v && typeof v === 'object')
        .map(([fbKey, v]) => ({ fbKey, ...v }));
      return { ok: true, items, deviceGroup: cfg.deviceGroup };
    } catch (err) {
      return { ok: false, reason: 'read_failed', error: err?.message || String(err) };
    }
  },

  /**
   * v7.50 — Apaga UMA chave de menuItems no Firebase RTDB e grava
   * tombstone (stableId-first). Para o cliente novo nunca mais ver.
   *
   * Args:
   *   fbKey: chave do nó no Firebase (geralmente o local-id numérico)
   *   stableId: UUID estável do item (autoritativo cross-device)
   *   name: nome do item (só para audit log)
   */
  deleteRemoteMenuItem: async ({ fbKey, stableId, name }) => {
    if (!currentDb) return { ok: false, reason: 'not_connected' };
    await waitForAuth();
    if (!isAuthenticated) return { ok: false, reason: 'not_authenticated' };
    const cfg = await getFirebaseConfig();
    if (!cfg.enabled) return { ok: false, reason: 'disabled' };
    const group = cfg.deviceGroup;
    try {
      // 1. Apaga o registro em si
      await remove(ref(currentDb, `${group}/menuItems/${fbKey}`));
      // 2. Grava tombstone REMOTO (chave preferencial: stableId)
      const tombKey = stableId || fbKey;
      try {
        await set(ref(currentDb, `${group}/__tombstones__/menuItems/${tombKey}`), {
          deletedAt: new Date().toISOString(),
          _writeUid: currentUid || null,
          _writeDevice: cachedNickname || null,
          ...(stableId ? { stableId } : {}),
          ...(name ? { name } : {}),
          reason: 'ghost_menu_cleanup',
        });
      } catch {}
      // 3. Grava tombstone LOCAL para que mesmo se voltar via sync, seja bloqueado
      try {
        await addTombstone('menuItems', {
          stableId: stableId || null,
          id: Number(fbKey) || null,
          name: name || null,
          reason: 'ghost_menu_cleanup',
        });
      } catch {}
      // 4. Apaga local também (se existir)
      try { await db.menuItems.delete(Number(fbKey)); } catch {}
      if (stableId) {
        try { await db.menuItems.where('stableId').equals(stableId).delete(); } catch {}
      }
      return { ok: true };
    } catch (err) {
      return { ok: false, reason: 'delete_failed', error: err?.message || String(err) };
    }
  },

  /**
   * v7.50 — Wipe cirúrgico: apaga TODA a árvore `{deviceGroup}/menuItems`
   * sem tocar em comandas/delivery/etc. Cria tombstones permanentes para
   * cada item no Dexie local para impedir ressurreição.
   *
   * Retorna `{ ok, deletedCount, tombstoneCount }`.
   */
  wipeRemoteMenuItems: async () => {
    if (!currentDb) return { ok: false, reason: 'not_connected' };
    await waitForAuth();
    if (!isAuthenticated) return { ok: false, reason: 'not_authenticated' };
    const cfg = await getFirebaseConfig();
    if (!cfg.enabled) return { ok: false, reason: 'disabled' };
    const group = cfg.deviceGroup;
    try {
      // 1. Lê tudo antes para criar tombstones
      const snap = await get(ref(currentDb, `${group}/menuItems`));
      const val = snap?.val() || {};
      const entries = Object.entries(val).filter(([, v]) => v && typeof v === 'object');
      let tombstoneCount = 0;
      for (const [fbKey, v] of entries) {
        try {
          await addTombstone('menuItems', {
            stableId: v.stableId || null,
            id: Number(fbKey) || null,
            name: v.name || null,
            reason: 'ghost_menu_wipe_all',
          });
          tombstoneCount++;
        } catch {}
      }
      // 2. Apaga TUDO no Firebase
      await set(ref(currentDb, `${group}/menuItems`), null);
      // 3. Apaga local também
      try { await db.menuItems.clear(); } catch {}
      // 4. Limpa caches in-memory para parar de tentar re-merge
      for (const k of Array.from(mergedVersions.keys())) {
        if (k.startsWith('menuItems#')) mergedVersions.delete(k);
      }
      return { ok: true, deletedCount: entries.length, tombstoneCount };
    } catch (err) {
      return { ok: false, reason: 'wipe_failed', error: err?.message || String(err) };
    }
  },

  /**
   * v7.53 — NUCLEAR WIPE: apaga `menuItems` em **múltiplos** deviceGroups
   * de uma vez. Resolve o caso de devices órfãos com UUID-group que
   * nunca migraram para `kaven-default`.
   *
   * Limpa:
   *   - `{currentDeviceGroup}/menuItems` + tombstones
   *   - `kaven-default/menuItems` + tombstones (sempre, mesmo se não for o atual)
   *   - Dexie local (menuItems.clear)
   *   - Tombstones byName de TODOS os GHOST_NAMES como blacklist eterna
   *
   * Args: extraGroups (array opcional) — pode informar grupos extras a
   * limpar (útil para limpar UUIDs órfãos detectados).
   */
  nuclearWipeMenuItems: async ({ extraGroups = [] } = {}) => {
    if (!currentDb) return { ok: false, reason: 'not_connected' };
    await waitForAuth();
    if (!isAuthenticated) return { ok: false, reason: 'not_authenticated' };
    const cfg = await getFirebaseConfig();
    if (!cfg.enabled) return { ok: false, reason: 'disabled' };
    const groups = new Set([cfg.deviceGroup, 'kaven-default', ...(Array.isArray(extraGroups) ? extraGroups : [])]);
    const results = {};
    for (const grp of groups) {
      try {
        const snap = await get(ref(currentDb, `${grp}/menuItems`));
        const val = snap?.val() || {};
        const count = Object.keys(val).length;
        await set(ref(currentDb, `${grp}/menuItems`), null);
        // Também apaga tombstones antigos pra começar do zero
        try { await set(ref(currentDb, `${grp}/__tombstones__/menuItems`), null); } catch {}
        results[grp] = { wiped: count };
      } catch (err) {
        results[grp] = { error: err?.message || String(err) };
      }
    }
    // Limpa Dexie local
    try { await db.menuItems.clear(); } catch {}
    // Limpa caches in-memory
    for (const k of Array.from(mergedVersions.keys())) {
      if (k.startsWith('menuItems#')) mergedVersions.delete(k);
    }
    // Limpa tombstones locais antigos pra começar fresh
    try { await db.config.delete('deletedTombstones__menuItems'); } catch {}
    try { await db.config.delete('deletedTombstones__menuItems__byName'); } catch {}
    return { ok: true, groupsWiped: results };
  },

  /**
   * v7.53 — Force PUSH: envia TODOS os itens do Dexie local pro Firebase
   * imediatamente, ignorando lastWriteVersion. Usado pelo botão "Sincronizar
   * agora" no /admin/limpeza-fantasma quando o operador suspeita que itens
   * locais não chegaram no remoto.
   */
  forcePushMenuItems: async () => {
    if (!currentDb) return { ok: false, reason: 'not_connected' };
    await waitForAuth();
    if (!isAuthenticated) return { ok: false, reason: 'not_authenticated' };
    const cfg = await getFirebaseConfig();
    if (!cfg.enabled) return { ok: false, reason: 'disabled' };
    const group = cfg.deviceGroup;
    try {
      const all = await db.menuItems.toArray();
      let pushed = 0;
      for (const item of all) {
        if (!item || !item.id) continue;
        const payload = {
          ...item,
          _writeUid: currentUid || null,
          _writeDevice: cachedNickname || null,
          _writeAt: new Date().toISOString(),
        };
        try {
          await set(ref(currentDb, `${group}/menuItems/${item.id}`), payload);
          pushed++;
        } catch {}
      }
      return { ok: true, pushed, total: all.length, deviceGroup: group };
    } catch (err) {
      return { ok: false, reason: 'push_failed', error: err?.message || String(err) };
    }
  },

  /**
   * v7.53 — Migra o deviceGroup local para o canônico (default: 'kaven-default').
   * Útil quando o device tem UUID-group órfão que nunca alinhou no boot.
   *
   * NÃO move dados — apenas troca a chave de namespace. Dados antigos no
   * UUID-group ficam órfãos e podem ser limpos com `nuclearWipeMenuItems`.
   */
  migrateDeviceGroupTo: async (targetGroup) => {
    if (!targetGroup || typeof targetGroup !== 'string') {
      return { ok: false, reason: 'invalid_target' };
    }
    try {
      const current = await db.config.get('deviceGroup');
      const previousGroup = current?.value || null;
      if (previousGroup === targetGroup) {
        return { ok: true, alreadyAligned: true, deviceGroup: targetGroup };
      }
      await db.config.put({ key: 'deviceGroup', value: targetGroup });
      // Limpa caches in-memory para os listeners reconectarem no novo grupo
      mergedVersions.clear();
      pendingWrites.clear();
      return { ok: true, previousGroup, newGroup: targetGroup, requiresReload: true };
    } catch (err) {
      return { ok: false, reason: 'migrate_failed', error: err?.message || String(err) };
    }
  },
};

/**
 * v8.05 — "Chute" de sincronização usado pelo keep-alive das telas
 * operacionais: força reconexão do socket Firebase e reenvia a fila de
 * escritas pendentes. Seguro de chamar repetidamente (idempotente).
 */
export function kickSync() {
  try { if (currentDb) goOnline(currentDb); } catch {}
  try { flushPendingWrites(); } catch {}
}
